import { CAMPOS_ACTIVO, type CampoActivo } from '@cmms/shared';
import { NuevoFabricante } from '../componentes/nuevo-fabricante.js';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BotonImportarCsv } from '../componentes/importar-csv.js';
import { BotonExportar } from '../componentes/exportar-datos.js';
import { IconoEditar, IconoEliminar, IconoLupa, IconoMas, IconoOjo, IconoOjoTachado, IconoQr } from '../componentes/iconos.js';
import {
  descargarQrActivo,
  etiquetaQrPng,
  hojaQrPng,
  imprimirHojaQr,
  type ActivoEtiquetaQr,
} from '../componentes/etiquetas-qr.js';
import { VisorQr } from '../componentes/visor-qr.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CODIGOS_ERROR,
  CRITICIDADES,
  ESTADOS_ACTIVO,
  ETIQUETA_CRITICIDAD,
  ETIQUETA_ESTADO_ACTIVO,
  ETIQUETA_TIPO_ACCESO,
  ETIQUETA_TIPO_GRUPO,
  TIPOS_ACCESO,
  TIPOS_GRUPO,
  formatearFecha,
  formatearFechaHora,
  type Criticidad,
  type EstadoActivo,
  type Pagina,
  type TipoAcceso,
  type TipoGrupo,
} from '@cmms/shared';
import { api, consulta, ErrorApi } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import { EscanerQr, hayCamara } from '../componentes/camara.js';
import {
  AccionesDialogo,
  Aviso,
  Cargando,
  CasillaVerificacion,
  Dialogo,
  DialogoConfirmar,
  Etiqueta,
  EstadoActivoEtiqueta,
  Indicador,
  MiniaturaImagenTabla,
  Paginacion,
  SeleccionMultiple,
  SubidaImagenes,
  TONO_ESTADO_ACTIVO,
  Vacio,
  mensajeError,
  type AdjuntoImagen,
} from '../componentes/piezas.js';
import {
  SelectorActivo,
  SelectorCatalogo,
  SelectorPersona,
  SelectorFecha,
  SelectorBuscable,
  SelectorLista,
  SelectorListaMultiple,
  SelectorUbicacion,
  SelectorUbicacionMultiple,
  useCatalogo,
  useGruposOpciones,
  useTiposActivo,
  useTiposUbicacion,
  useUbicaciones,
  useUsuariosOpciones,
  type NodoUbicacion,
} from '../componentes/selectores.js';
import { useEsMovil } from '../servicios/responsive.js';
import { DialogoTabletsSede } from '../componentes/tablets-sede.js';

/** Los campos de texto opcionales viajan como cadena: el contrato no los admite nulos. */
function texto(valor: string): string {
  return valor.trim();
}

/** Las referencias sí son nulables: vacío significa «sin relación». */
function referencia(valor: string): string | null {
  return valor === '' ? null : valor;
}

// ─── Activos (§5) ────────────────────────────────────────────────────────────

interface Activo {
  id: string;
  numero: number;
  nombre: string;
  tiposActivo: { id: string; nombre: string }[];
  atributos?: Record<string, string>;
  ubicacionId: string | null;
  ubicacionNombre: string | null;
  ubicacionRaiz: string | null;
  activoPadreId: string | null;
  activoPadreNombre: string | null;
  criticidad: Criticidad | null;
  estado: EstadoActivo;
  motivoInactividadId: string | null;
  motivoInactividad: string | null;
  descripcion: string | null;
  fabricanteId: string | null;
  fabricante: string | null;
  modeloId: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  codigoQr: string | null;
  expiracionGarantia: string | null;
  ocultoEl: string | null;
  fotoUrl: string | null;
}

interface BorradorFiltrosActivo {
  estado: string[];
  criticidad: string[];
  buscar: string;
  ubicacionId: string[];
  fabricanteId: string[];
  modeloId: string;
  tipoActivoId: string[];
  garantiaVencida: boolean;
  incluirOcultos: boolean;
}

/** Filtros del listado de activos. Se guardan en la sesión para que sobrevivan al ir a una ficha y volver. */
const CLAVE_FILTROS_ACTIVOS = 'cmms.activos.filtros';

interface FiltrosActivos {
  pagina: number;
  estado: string[];
  criticidad: string[];
  buscar: string;
  ubicacionId: string[];
  fabricanteId: string[];
  modeloId: string;
  tipoActivoId: string[];
  garantiaVencida: boolean;
  incluirOcultos: boolean;
}

const FILTROS_ACTIVOS_INICIALES: FiltrosActivos = {
  pagina: 1,
  estado: [],
  criticidad: [],
  buscar: '',
  ubicacionId: [],
  fabricanteId: [],
  modeloId: '',
  tipoActivoId: [],
  garantiaVencida: false,
  incluirOcultos: false,
};

function leerFiltrosActivos(): FiltrosActivos {
  try {
    const guardados = JSON.parse(sessionStorage.getItem(CLAVE_FILTROS_ACTIVOS) ?? '{}') as Partial<FiltrosActivos>;
    return { ...FILTROS_ACTIVOS_INICIALES, ...guardados };
  } catch {
    // Sin acceso al almacenamiento (modo privado, datos bloqueados): se abre sin filtros.
    return FILTROS_ACTIVOS_INICIALES;
  }
}

export function ListaActivos(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [guardados] = useState(leerFiltrosActivos);
  const [pagina, setPagina] = useState(guardados.pagina);
  // Cada filtro admite varias opciones a la vez (§ pedido): "Estado: Abierta
  // o En curso" en vez de una sola. Entre filtros distintos siguen
  // combinándose con Y (Estado Y Ubicación), como antes.
  const [estado, setEstado] = useState<string[]>(guardados.estado);
  const [criticidad, setCriticidad] = useState<string[]>(guardados.criticidad);
  const [buscar, setBuscar] = useState(guardados.buscar);
  const [ubicacionId, setUbicacionId] = useState<string[]>(guardados.ubicacionId);
  const [fabricanteId, setFabricanteId] = useState<string[]>(guardados.fabricanteId);
  const [modeloId, setModeloId] = useState(guardados.modeloId);
  const [tipoActivoId, setTipoActivoId] = useState<string[]>(guardados.tipoActivoId);
  const [garantiaVencida, setGarantiaVencida] = useState(guardados.garantiaVencida);
  // Los ocultos (§ pedido: distinto de eliminar) salen de la vista por
  // defecto, igual que hoy pasa con los eliminados — este interruptor los
  // vuelve a traer para poder revertir el ocultamiento.
  const [incluirOcultos, setIncluirOcultos] = useState(guardados.incluirOcultos);

  useEffect(() => {
    const filtros: FiltrosActivos = { pagina, estado, criticidad, buscar, ubicacionId, fabricanteId, modeloId, tipoActivoId, garantiaVencida, incluirOcultos };
    try {
      sessionStorage.setItem(CLAVE_FILTROS_ACTIVOS, JSON.stringify(filtros));
    } catch {
      // Si no se puede guardar, el listado sigue funcionando sin recordar los filtros.
    }
  }, [pagina, estado, criticidad, buscar, ubicacionId, fabricanteId, modeloId, tipoActivoId, garantiaVencida, incluirOcultos]);
  const [editando, setEditando] = useState<Activo | null | undefined>(undefined);
  const [eliminando, setEliminando] = useState<Activo | null>(null);
  const [ocultando, setOcultando] = useState<Activo | null>(null);
  // Selección para descargar los QR de varios activos a la vez. Se guarda por id
  // para que sobreviva a cambiar de página, junto con los datos de su etiqueta: la
  // API solo trae la página visible y el PDF tiene que incluir todos los marcados.
  const [visorQr, setVisorQr] = useState<{ imagen: string; titulo: string } | null>(null);
  const [seleccionados, setSeleccionados] = useState<Record<string, ActivoEtiquetaQr>>({});
  const elegidosQr = Object.values(seleccionados).filter((a) => a.codigoQr !== '');
  const estaMarcado = (id: string): boolean => id in seleccionados;
  const [errorQr, setErrorQr] = useState<string | null>(null);
  const [generandoQr, setGenerandoQr] = useState(false);

  const escribe = puede('activo:escribir');

  function datosQr(a: Activo): ActivoEtiquetaQr {
    return { codigoQr: a.codigoQr ?? '', numero: a.numero, nombre: a.nombre, ubicacionNombre: a.ubicacionNombre };
  }

  function alternarSeleccion(a: Activo): void {
    setSeleccionados((actual) => {
      const copia = { ...actual };
      if (a.id in copia) delete copia[a.id];
      else copia[a.id] = datosQr(a);
      return copia;
    });
  }

  async function descargarQrSeleccionados(): Promise<void> {
    if (elegidosQr.length === 0) return;
    setErrorQr(null);
    setGenerandoQr(true);
    try {
      if (esMovil) {
        setVisorQr({ imagen: await hojaQrPng(elegidosQr), titulo: `${elegidosQr.length} códigos QR` });
      } else {
        await imprimirHojaQr(elegidosQr);
      }
    } catch (error) {
      setErrorQr(mensajeError(error, 'No se pudieron generar los QR.'));
    } finally {
      setGenerandoQr(false);
    }
  }

  async function descargarQrDeUno(a: Activo): Promise<void> {
    if (!a.codigoQr) return;
    setErrorQr(null);
    try {
      const qr = { codigoQr: a.codigoQr, numero: a.numero, nombre: a.nombre, ubicacionNombre: a.ubicacionNombre };
      if (esMovil) {
        setVisorQr({ imagen: await etiquetaQrPng(qr), titulo: `#${a.numero} ${a.nombre}` });
      } else {
        await descargarQrActivo(qr);
      }
    } catch (error) {
      setErrorQr(mensajeError(error, 'No se pudo descargar el QR.'));
    }
  }

  // La ficha de detalle (solo lectura) enlaza acá con `?editar=<id>` para
  // abrir el formulario de edición sin duplicarlo: se consume una sola vez
  // y se limpia de la URL para no reabrirse en cada refresco.
  useEffect(() => {
    const idEditar = parametros.get('editar');
    if (!idEditar || !escribe) return;
    setParametros((p) => {
      p.delete('editar');
      return p;
    }, { replace: true });
    void cliente.fetchQuery({ queryKey: ['activo', idEditar], queryFn: () => api.get<Activo>(`/activos/${idEditar}`) }).then(setEditando);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametros, escribe]);

  const { data: ubicaciones } = useUbicaciones();
  const { data: tipos } = useTiposActivo();
  const { data: fabricantes } = useCatalogo('FABRICANTE');

  const { data, isLoading } = useQuery({
    queryKey: [
      'activos',
      pagina,
      estado,
      criticidad,
      buscar,
      ubicacionId,
      fabricanteId,
      modeloId,
      tipoActivoId,
      garantiaVencida,
      incluirOcultos,
    ],
    queryFn: () =>
      api.get<Pagina<Activo>>(
        `/activos${consulta({
          pagina,
          tamano: 25,
          estado,
          criticidad,
          buscar,
          ubicacionId,
          fabricanteId,
          modeloId,
          tipoActivoId,
          garantiaVencida: garantiaVencida || undefined,
          incluirOcultos: incluirOcultos || undefined,
        })}`,
      ),
  });

  const filtrosActivos = [
    ubicacionId.length > 0,
    fabricanteId.length > 0,
    modeloId !== '',
    tipoActivoId.length > 0,
    garantiaVencida,
    incluirOcultos,
  ].filter(Boolean).length;

  const limpiarFiltrosAvanzados = (): void => {
    setUbicacionId([]);
    setFabricanteId([]);
    setModeloId('');
    setTipoActivoId([]);
    setGarantiaVencida(false);
    setIncluirOcultos(false);
    setPagina(1);
  };

  const cantidadFiltrosAplicados = filtrosActivos + (estado.length > 0 ? 1 : 0) + (criticidad.length > 0 ? 1 : 0);

  // En móvil todos los filtros se editan juntos en un modal y solo se aplican
  // al confirmar, en vez de disparar una consulta por tecla (§«más filtros»).
  const [modalFiltrosAbierto, setModalFiltrosAbierto] = useState(false);
  const [borrador, setBorrador] = useState<BorradorFiltrosActivo | null>(null);

  function abrirModalFiltros(): void {
    setBorrador({
      estado,
      criticidad,
      buscar,
      ubicacionId,
      fabricanteId,
      modeloId,
      tipoActivoId,
      garantiaVencida,
      incluirOcultos,
    });
    setModalFiltrosAbierto(true);
  }

  function establecerBorrador<K extends keyof BorradorFiltrosActivo>(campo: K, valor: BorradorFiltrosActivo[K]): void {
    setBorrador((b) => (b ? { ...b, [campo]: valor } : b));
  }

  function limpiarBorrador(): void {
    setBorrador({
      estado: [],
      criticidad: [],
      buscar: '',
      ubicacionId: [],
      fabricanteId: [],
      modeloId: '',
      tipoActivoId: [],
      garantiaVencida: false,
      incluirOcultos: false,
    });
  }

  function aplicarBorrador(): void {
    if (!borrador) return;
    setEstado(borrador.estado);
    setCriticidad(borrador.criticidad);
    setBuscar(borrador.buscar);
    setUbicacionId(borrador.ubicacionId);
    setFabricanteId(borrador.fabricanteId);
    setModeloId(borrador.modeloId);
    setTipoActivoId(borrador.tipoActivoId);
    setGarantiaVencida(borrador.garantiaVencida);
    setIncluirOcultos(borrador.incluirOcultos);
    setPagina(1);
    setModalFiltrosAbierto(false);
  }

  const refrescar = async (): Promise<void> => {
    await cliente.invalidateQueries({ queryKey: ['activos'] });
    await cliente.invalidateQueries({ queryKey: ['activos-opciones'] });
    await cliente.invalidateQueries({ queryKey: ['ubicaciones-arbol'] });
  };

  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/activos/${id}`),
    onSuccess: async () => {
      await refrescar();
      setEliminando(null);
    },
  });

  const ocultar = useMutation({
    mutationFn: (id: string) => api.post(`/activos/${id}/ocultar`),
    onSuccess: async () => {
      await refrescar();
      setOcultando(null);
    },
  });

  const mostrar = useMutation({
    mutationFn: (id: string) => api.post(`/activos/${id}/mostrar`),
    onSuccess: () => refrescar(),
  });

  // KPIs por estado (mismo patrón que OT, § pedido: mismo color que la
  // etiqueta, clic filtra la lista por ese estado): cuentan el total del
  // tenant, no solo la página actual, para que el número no cambie de
  // significado al seleccionar otro badge.
  const { data: resumen } = useQuery({
    queryKey: ['activos-resumen'],
    queryFn: () => api.get<{ total: number; porEstado: { estado: EstadoActivo; cantidad: number }[]; sinCriticidad: number }>(
      '/activos/resumen',
    ),
  });
  const cantidadPorEstado = (e: EstadoActivo): number => resumen?.porEstado.find((p) => p.estado === e)?.cantidad ?? 0;

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Activos</h1>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar activos"
            placeholder="Buscar Activos"
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
          />
        </div>
        <BotonExportar
          ruta="/activos"
          filtros={{
            estado,
            criticidad,
            buscar,
            ubicacionId,
            fabricanteId,
            modeloId,
            tipoActivoId,
            garantiaVencida: garantiaVencida || undefined,
            incluirOcultos: incluirOcultos || undefined,
          }}
        />
        {escribe ? (
          <>
            <BotonImportarCsv ruta="activos" alTerminar={() => void refrescar()} />
            <button type="button" className="boton oculto-en-movil" onClick={() => setEditando(null)}>
              + Nuevo Activo
            </button>
            <button
              type="button"
              className="boton-flotante-crear"
              aria-label="Nuevo activo"
              onClick={() => setEditando(null)}
            >
              <IconoMas />
            </button>
          </>
        ) : null}
      </div>

      <div className="rejilla-indicadores">
        <Indicador
          titulo="TOTAL"
          valor={resumen?.total ?? '—'}
          activo={estado.length === 0}
          className="col-ocultar-movil"
          onClick={() => {
            setEstado([]);
            setPagina(1);
          }}
        />
        {ESTADOS_ACTIVO.map((e) => (
          <Indicador
            key={e}
            titulo={ETIQUETA_ESTADO_ACTIVO[e].toUpperCase()}
            valor={cantidadPorEstado(e)}
            tono={TONO_ESTADO_ACTIVO[e]}
            activo={estado.includes(e)}
            className={e === 'NO_REALIZAR_SEGUIMIENTO' ? 'col-ocultar-movil' : undefined}
            onClick={() => {
              // Un KPI a la vez: tocar otro reemplaza al anterior, y tocar el
              // activo lo quita (igual que en la lista de OT).
              setEstado((actual) => (actual.length === 1 && actual[0] === e ? [] : [e]));
              setPagina(1);
            }}
          />
        ))}
        {/* §5.2: la criticidad es la variable que permite priorizar el backlog, y
            hoy está sin poblar. Exponerlo es el primer paso para corregirlo. */}
        <Indicador
          titulo="SIN CRITICIDAD"
          valor={resumen?.sinCriticidad ?? 0}
          nota={(resumen?.sinCriticidad ?? 0) > 0 ? 'Impide priorizar el backlog' : 'Completo'}
          className="col-ocultar-movil"
        />
      </div>

      <button type="button" className="boton secundario solo-movil boton-filtros-movil" onClick={abrirModalFiltros}>
        Filtros{cantidadFiltrosAplicados > 0 ? ` (${cantidadFiltrosAplicados})` : ''}
      </button>

      <div className="filtros filtros-escritorio">
        <SelectorListaMultiple
          id="activos-filtro-estado-rapido"
          buscador
          etiqueta="Estado"
          seleccion={estado}
          alCambiar={(v) => {
            setEstado(v);
            setPagina(1);
          }}
          opciones={ESTADOS_ACTIVO.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_ACTIVO[e] }))}
          textoVacio="Todos los estados"
        />
        <SelectorListaMultiple
          id="activos-filtro-criticidad-rapido"
          buscador
          etiqueta="Criticidad"
          seleccion={criticidad}
          alCambiar={(v) => {
            setCriticidad(v);
            setPagina(1);
          }}
          opciones={CRITICIDADES.map((c) => ({ id: c, nombre: ETIQUETA_CRITICIDAD[c] }))}
          textoVacio="Todas las criticidades"
        />
      </div>

      <details className="panel filtros-avanzados filtros-escritorio" style={{ marginBottom: 16 }}>
        <summary>
          Filtros{filtrosActivos > 0 ? ` (${filtrosActivos})` : ''}
        </summary>
        <div className="panel-cuerpo">
          <div className="rejilla-campos">
            <SelectorUbicacionMultiple
              id="activos-filtro-ubicacion"
              buscador
              etiqueta="Ubicación"
              seleccion={ubicacionId}
              alCambiar={(v) => {
                setUbicacionId(v);
                setPagina(1);
              }}
              ubicaciones={ubicaciones}
              textoVacio="Todas las ubicaciones"
            />
            <SelectorListaMultiple
              id="activos-filtro-fabricante"
              buscador
              etiqueta="Fabricante"
              seleccion={fabricanteId}
              textoVacio="Todos los fabricantes"
              opciones={(fabricantes ?? []).map((f) => ({ id: f.id, nombre: f.nombre }))}
              alCambiar={(v) => {
                setFabricanteId(v);
                // Con más de un fabricante elegido no hay un único padre al
                // que acotar el catálogo de modelos — se limpia en vez de
                // dejar un modelo que ya no corresponde a la selección.
                if (v.length !== 1) setModeloId('');
                setPagina(1);
              }}
            />
            <SelectorCatalogo
              id="activos-filtro-modelo"
              buscador
              codigo="MODELO"
              etiqueta="Modelo"
              valor={modeloId}
              textoVacio={
                fabricanteId.length === 0
                  ? 'Elija primero un fabricante'
                  : fabricanteId.length > 1
                    ? 'Elija un solo fabricante para filtrar por modelo'
                    : 'Todos los modelos'
              }
              padreId={fabricanteId.length === 1 ? fabricanteId[0] : undefined}
              habilitado={fabricanteId.length === 1}
              alCambiar={(v) => {
                setModeloId(v);
                setPagina(1);
              }}
            />
            <SelectorListaMultiple
              id="activos-filtro-tipo"
              buscador
              etiqueta="Tipo de activo"
              seleccion={tipoActivoId}
              alCambiar={(v) => {
                setTipoActivoId(v);
                setPagina(1);
              }}
              opciones={(tipos ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
              textoVacio="Todos los tipos"
            />
          </div>

          <label className="casilla" style={{ marginTop: 4 }}>
            <input
              type="checkbox"
              checked={garantiaVencida}
              onChange={(e) => {
                setGarantiaVencida(e.target.checked);
                setPagina(1);
              }}
            />
            Solo con garantía vencida
          </label>

          <label className="casilla" style={{ marginTop: 4 }}>
            <input
              type="checkbox"
              checked={incluirOcultos}
              onChange={(e) => {
                setIncluirOcultos(e.target.checked);
                setPagina(1);
              }}
            />
            Ver solo ocultos
          </label>

          {filtrosActivos > 0 ? (
            <button
              type="button"
              className="boton secundario pequeno"
              style={{ marginTop: 12 }}
              onClick={limpiarFiltrosAvanzados}
            >
              Limpiar filtros
            </button>
          ) : null}
        </div>
      </details>

      {modalFiltrosAbierto && borrador ? (
        <Dialogo titulo="Filtros" ancho={560} alCerrar={() => setModalFiltrosAbierto(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="campo">
              <label htmlFor="mf-activos-buscar">Buscar</label>
              <input
                id="mf-activos-buscar"
                placeholder="Buscar por nombre o número de serie…"
                value={borrador.buscar}
                onChange={(e) => establecerBorrador('buscar', e.target.value)}
              />
            </div>

            <div className="rejilla-campos">
              <SelectorListaMultiple
                id="mf-activos-estado"
                buscador
                etiqueta="Estado"
                seleccion={borrador.estado}
                alCambiar={(v) => establecerBorrador('estado', v)}
                opciones={ESTADOS_ACTIVO.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_ACTIVO[e] }))}
                textoVacio="Todos los estados"
              />
              <SelectorListaMultiple
                id="mf-activos-criticidad"
                buscador
                etiqueta="Criticidad"
                seleccion={borrador.criticidad}
                alCambiar={(v) => establecerBorrador('criticidad', v)}
                opciones={CRITICIDADES.map((c) => ({ id: c, nombre: ETIQUETA_CRITICIDAD[c] }))}
                textoVacio="Todas las criticidades"
              />
              <SelectorUbicacionMultiple
                id="mf-activos-ubicacion"
                buscador
                etiqueta="Ubicación"
                seleccion={borrador.ubicacionId}
                alCambiar={(v) => establecerBorrador('ubicacionId', v)}
                ubicaciones={ubicaciones}
                textoVacio="Todas las ubicaciones"
              />
              <SelectorListaMultiple
                id="mf-activos-fabricante"
                buscador
                etiqueta="Fabricante"
                seleccion={borrador.fabricanteId}
                textoVacio="Todos los fabricantes"
                opciones={(fabricantes ?? []).map((f) => ({ id: f.id, nombre: f.nombre }))}
                alCambiar={(v) => {
                  establecerBorrador('fabricanteId', v);
                  if (v.length !== 1) establecerBorrador('modeloId', '');
                }}
              />
              <SelectorCatalogo
                id="mf-activos-modelo"
                buscador
                codigo="MODELO"
                etiqueta="Modelo"
                valor={borrador.modeloId}
                textoVacio={
                  borrador.fabricanteId.length === 0
                    ? 'Elija primero un fabricante'
                    : borrador.fabricanteId.length > 1
                      ? 'Elija un solo fabricante para filtrar por modelo'
                      : 'Todos los modelos'
                }
                padreId={borrador.fabricanteId.length === 1 ? borrador.fabricanteId[0] : undefined}
                habilitado={borrador.fabricanteId.length === 1}
                alCambiar={(v) => establecerBorrador('modeloId', v)}
              />
              <SelectorListaMultiple
                id="mf-activos-tipo"
                buscador
                etiqueta="Tipo de activo"
                seleccion={borrador.tipoActivoId}
                alCambiar={(v) => establecerBorrador('tipoActivoId', v)}
                opciones={(tipos ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
                textoVacio="Todos los tipos"
              />
            </div>

            <label className="casilla">
              <input
                type="checkbox"
                checked={borrador.garantiaVencida}
                onChange={(e) => establecerBorrador('garantiaVencida', e.target.checked)}
              />
              Solo con garantía vencida
            </label>

            <label className="casilla">
              <input
                type="checkbox"
                checked={borrador.incluirOcultos}
                onChange={(e) => establecerBorrador('incluirOcultos', e.target.checked)}
              />
              Ver solo ocultos
            </label>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 4, flexWrap: 'wrap' }}>
              <button type="button" className="boton secundario" onClick={limpiarBorrador}>
                Limpiar
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="boton secundario" onClick={() => setModalFiltrosAbierto(false)}>
                  Cancelar
                </button>
                <button type="button" className="boton" onClick={aplicarBorrador}>
                  Aplicar filtros
                </button>
              </div>
            </div>
          </div>
        </Dialogo>
      ) : null}

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.datos.length === 0 ? (
          <Vacio
            titulo="Sin activos en esta vista"
            detalle={escribe ? 'Registre el primero con «Nuevo activo».' : undefined}
          />
        ) : null}

        {data && data.datos.length > 0 ? (
          <>
            {elegidosQr.length > 0 ? (
              <div className="barra-seleccion" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '8px 12px' }}>
                <span>{elegidosQr.length} seleccionado{elegidosQr.length === 1 ? '' : 's'}</span>
                <button type="button" className="boton pequeno" disabled={generandoQr} onClick={() => void descargarQrSeleccionados()}>
                  {generandoQr ? 'Generando…' : 'Descargar QRs (PDF)'}
                </button>
                <button type="button" className="boton secundario pequeno" onClick={() => setSeleccionados({})}>
                  Limpiar selección
                </button>
              </div>
            ) : null}
            {errorQr ? <Aviso tono="error">{errorQr}</Aviso> : null}
            {esMovil ? (
              <div className="tarjetas-ot">
                {data.datos.map((a) => (
                  <div
                    key={a.id}
                    className="tarjeta-ot"
                    role="button"
                    tabIndex={0}
                    onClick={() => navegar(`/activos/${a.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navegar(`/activos/${a.id}`);
                    }}
                  >
                    <MiniaturaImagenTabla url={a.fotoUrl} nombre={a.nombre} />
                    <div className="tarjeta-ot-cuerpo">
                      <div className="tarjeta-ot-cabecera">
                        <span className="numero-registro">#{a.numero}</span>
                        <EstadoActivoEtiqueta estado={a.estado} />
                        {a.ocultoEl ? <Etiqueta tono="gris">Oculto</Etiqueta> : null}
                      </div>
                      <div className="tarjeta-ot-titulo">{a.nombre}</div>
                      {a.activoPadreNombre ? (
                        <div className="tarjeta-ot-nota">Pertenece a {a.activoPadreNombre}</div>
                      ) : null}
                      <div className="tarjeta-ot-datos">
                        {a.criticidad ? (
                          <Etiqueta tono={a.criticidad === 'CRITICO' ? 'roja' : a.criticidad === 'IMPORTANTE' ? 'ambar' : 'gris'}>
                            {ETIQUETA_CRITICIDAD[a.criticidad]}
                          </Etiqueta>
                        ) : null}
                        {a.ubicacionNombre ? <span>{a.ubicacionNombre}</span> : null}
                      </div>
                    </div>
                    <div className="acciones-fila" onClick={(e) => e.stopPropagation()}>
                      <CasillaVerificacion
                        marcada={estaMarcado(a.id)}
                        alCambiar={() => alternarSeleccion(a)}
                        etiqueta={`Seleccionar activo #${a.numero}`}
                      />
                      {a.codigoQr ? (
                        <button
                          type="button"
                          className="boton-icono info"
                          title="Descargar QR"
                          aria-label="Descargar QR"
                          onClick={() => void descargarQrDeUno(a)}
                        >
                          <IconoQr />
                        </button>
                      ) : null}
                    </div>
                    {escribe ? (
                      <div className="acciones-fila" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="boton-icono info"
                          title="Editar"
                          aria-label="Editar"
                          onClick={() => setEditando(a)}
                        >
                          <IconoEditar />
                        </button>
                        <button
                          type="button"
                          className="boton-icono advertencia"
                          title={a.ocultoEl ? 'Mostrar' : 'Ocultar'}
                          aria-label={a.ocultoEl ? 'Mostrar' : 'Ocultar'}
                          onClick={() => (a.ocultoEl ? mostrar.mutate(a.id) : setOcultando(a))}
                        >
                          {a.ocultoEl ? <IconoOjo /> : <IconoOjoTachado />}
                        </button>
                        <button
                          type="button"
                          className="boton-icono peligro"
                          title="Eliminar"
                          aria-label="Eliminar"
                          onClick={() => setEliminando(a)}
                        >
                          <IconoEliminar />
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} onClick={(e) => e.stopPropagation()}>
                        <CasillaVerificacion
                          marcada={data.datos.every((a) => estaMarcado(a.id))}
                          alCambiar={() => {
                            const todasMarcadas = data.datos.every((a) => estaMarcado(a.id));
                            setSeleccionados((actual) => {
                              const copia = { ...actual };
                              for (const a of data.datos) {
                                if (todasMarcadas) delete copia[a.id];
                                else copia[a.id] = datosQr(a);
                              }
                              return copia;
                            });
                          }}
                          etiqueta="Seleccionar todos los activos de esta página"
                        />
                      </th>
                      <th>Foto</th>
                      <th>N.º</th>
                      <th>Nombre</th>
                      <th>Estado</th>
                      <th>Criticidad</th>
                      <th>Ubicación</th>
                      <th>Tipos</th>
                      <th>N.º de serie</th>
                      <th style={{ width: 150 }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.datos.map((a) => (
                      <tr key={a.id} className="fila-clicable" onClick={() => navegar(`/activos/${a.id}`)}>
                        <td onClick={(e) => e.stopPropagation()}>
                          <CasillaVerificacion
                            marcada={estaMarcado(a.id)}
                            alCambiar={() => alternarSeleccion(a)}
                            etiqueta={`Seleccionar activo #${a.numero}`}
                          />
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <MiniaturaImagenTabla url={a.fotoUrl} nombre={a.nombre} />
                        </td>
                        <td className="numero-registro">#{a.numero}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{a.nombre}</div>
                          {a.activoPadreNombre ? (
                            <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>
                              Pertenece a {a.activoPadreNombre}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <EstadoActivoEtiqueta estado={a.estado} />
                          {a.ocultoEl ? (
                            <div style={{ marginTop: 4 }}>
                              <Etiqueta tono="gris">Oculto</Etiqueta>
                            </div>
                          ) : null}
                          {a.motivoInactividad ? (
                            <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{a.motivoInactividad}</div>
                          ) : null}
                        </td>
                        <td>
                          {a.criticidad ? (
                            <Etiqueta tono={a.criticidad === 'CRITICO' ? 'roja' : a.criticidad === 'IMPORTANTE' ? 'ambar' : 'gris'}>
                              {ETIQUETA_CRITICIDAD[a.criticidad]}
                            </Etiqueta>
                          ) : (
                            <span style={{ color: 'var(--texto-tenue)' }}>Sin definir</span>
                          )}
                        </td>
                        <td>
                          {a.ubicacionNombre ?? '—'}
                          {a.ubicacionRaiz ? (
                            <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{a.ubicacionRaiz}</div>
                          ) : null}
                        </td>
                        <td>{a.tiposActivo.map((t) => t.nombre).join(', ') || '—'}</td>
                        <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>{a.numeroSerie ?? '—'}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="acciones-fila">
                            {a.codigoQr ? (
                              <button
                                type="button"
                                className="boton-icono info"
                                title="Descargar QR"
                                aria-label="Descargar QR"
                                onClick={() => void descargarQrDeUno(a)}
                              >
                                <IconoQr />
                              </button>
                            ) : null}
                            {escribe ? (
                              <>
                                <button
                                  type="button"
                                  className="boton-icono info"
                                  title="Editar"
                                  aria-label="Editar"
                                  onClick={() => setEditando(a)}
                                >
                                  <IconoEditar />
                                </button>
                                <button
                                  type="button"
                                  className="boton-icono advertencia"
                                  title={a.ocultoEl ? 'Mostrar' : 'Ocultar'}
                                  aria-label={a.ocultoEl ? 'Mostrar' : 'Ocultar'}
                                  onClick={() => (a.ocultoEl ? mostrar.mutate(a.id) : setOcultando(a))}
                                >
                                  {a.ocultoEl ? <IconoOjo /> : <IconoOjoTachado />}
                                </button>
                                <button
                                  type="button"
                                  className="boton-icono peligro"
                                  title="Eliminar"
                                  aria-label="Eliminar"
                                  onClick={() => setEliminando(a)}
                                >
                                  <IconoEliminar />
                                </button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Paginacion pagina={data.pagina} tamano={data.tamano} total={data.total} onCambiar={setPagina} />
          </>
        ) : null}
      </div>

      {editando !== undefined ? (
        <FormularioActivo
          activo={editando}
          alCerrar={() => setEditando(undefined)}
          alGuardar={async () => {
            await refrescar();
            setEditando(undefined);
          }}
        />
      ) : null}

      {visorQr ? <VisorQr imagen={visorQr.imagen} titulo={visorQr.titulo} alCerrar={() => setVisorQr(null)} /> : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar activo"
          mensaje={`Esta acción es permanente y no tiene «deshacer»: se retirará «${eliminando.nombre}» del inventario. Las órdenes de trabajo que lo referencian conservan su historial. Si en realidad quiere poder recuperarlo más adelante, cierre este diálogo y use «Ocultar» en su lugar.`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(null);
          }}
          alConfirmar={() => eliminar.mutate(eliminando.id)}
        />
      ) : null}

      {ocultando ? (
        <DialogoConfirmar
          titulo="Ocultar activo"
          mensaje={`«${ocultando.nombre}» dejará de aparecer en los listados y selectores (por ejemplo, al elegir el activo de una OT nueva), pero conserva su historial y puede volver a mostrarse en cualquier momento con «Ver solo ocultos» → «Mostrar».`}
          cargando={ocultar.isPending}
          error={ocultar.error}
          alCerrar={() => {
            ocultar.reset();
            setOcultando(null);
          }}
          alConfirmar={() => ocultar.mutate(ocultando.id)}
        />
      ) : null}
    </>
  );
}

/**
 * Alta y edición de un activo (§5.3).
 *
 * `null` en `activo` es un alta; un objeto es la edición de ese registro. El
 * formulario es el mismo porque el contrato del servidor también lo es: `PUT`
 * espera la ficha completa, no un parche.
 */
/** Tarjeta de un grupo del formulario de activo: título fijo y contenido agrupado. */
function SeccionFormulario({ titulo, children }: { titulo: string; children: ReactNode }): JSX.Element {
  return (
    <section className="panel" style={{ marginBottom: 12 }}>
      <div className="panel-cabecera">
        <h2>{titulo}</h2>
      </div>
      <div className="panel-cuerpo seccion-columnas">{children}</div>
    </section>
  );
}

interface AsignacionActivoForm {
  personaId: string;
  personaNombre: string;
  desde: string;
  hasta: string | null;
}

interface HistorialAsignacionesActivo {
  vigente: AsignacionActivoForm | null;
  historial: AsignacionActivoForm[];
}

function FormularioActivo({
  activo,
  alCerrar,
  alGuardar,
}: {
  activo: Activo | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(activo?.nombre ?? '');
  const [tiposActivo, setTiposActivo] = useState<string[]>(activo?.tiposActivo.slice(0, 1).map((t) => t.id) ?? []);
  const [ubicacionId, setUbicacionId] = useState(activo?.ubicacionId ?? '');
  const [activoPadreId, setActivoPadreId] = useState(activo?.activoPadreId ?? '');
  const [criticidad, setCriticidad] = useState<Criticidad>(activo?.criticidad ?? 'NORMAL');
  const [estado, setEstado] = useState<EstadoActivo>(activo?.estado ?? 'EN_LINEA');
  const [motivoInactividadId, setMotivoInactividadId] = useState(activo?.motivoInactividadId ?? '');
  const [descripcion, setDescripcion] = useState(activo?.descripcion ?? '');
  const [fabricanteId, setFabricanteId] = useState(activo?.fabricanteId ?? '');
  const [modeloId, setModeloId] = useState(activo?.modeloId ?? '');
  const [numeroSerie, setNumeroSerie] = useState(activo?.numeroSerie ?? '');
  // Vacío = el backend genera uno al crear. Con valor = se vincula una etiqueta
  // QR ya impresa (se teclea o se escanea).
  const [codigoQr, setCodigoQr] = useState(activo?.codigoQr ?? '');
  const [escaneandoQr, setEscaneandoQr] = useState(false);
  const [expiracionGarantia, setExpiracionGarantia] = useState(
    activo?.expiracionGarantia ? activo.expiracionGarantia.slice(0, 10) : '',
  );
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);
  const [atributos, setAtributos] = useState<Record<string, string>>(activo?.atributos ?? {});
  const cliente = useQueryClient();
  // Persona que lo usa: se carga con la asignación vigente y se guarda junto con el activo.
  const { data: asignacion } = useQuery({
    queryKey: ['activo-asignaciones', activo?.id],
    queryFn: () => api.get<HistorialAsignacionesActivo>(`/activos/${activo?.id}/asignaciones`),
    enabled: activo !== null && activo !== undefined,
  });
  const personaVigente = asignacion?.vigente ?? null;
  const [personaId, setPersonaId] = useState('');
  const personaCargada = useRef(false);
  useEffect(() => {
    if (asignacion && !personaCargada.current) {
      personaCargada.current = true;
      setPersonaId(asignacion.vigente?.personaId ?? '');
    }
  }, [asignacion]);
  const anteriores = asignacion?.historial.filter((a) => a.hasta !== null) ?? [];
  const [creandoFabricante, setCreandoFabricante] = useState(false);

  const { data: ubicaciones } = useUbicaciones();
  const { data: tipos } = useTiposActivo();
  const { data: fabricantes } = useCatalogo('FABRICANTE');

  // Campos de los tipos elegidos, sin repetir. Obligatorio si lo es en algún tipo.
  const camposDelFormulario = new Map<CampoActivo, boolean>();
  for (const t of (tipos ?? []).filter((t) => tiposActivo.includes(t.id))) {
    for (const c of t.campos) {
      const campo = c.campo as CampoActivo;
      camposDelFormulario.set(campo, (camposDelFormulario.get(campo) ?? false) || c.obligatorio);
    }
  }

  // Al crear, el resto del formulario aparece cuando ya se eligió un tipo de activo.
  const mostrarResto = activo != null || tiposActivo.length > 0;

  function establecerAtributo(campo: string, valor: string): void {
    setAtributos((previos) => ({ ...previos, [campo]: valor }));
  }

  const { data: fotosExistentes } = useQuery({
    queryKey: ['adjuntos', 'ACTIVO', activo?.id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'ACTIVO', entidadId: activo?.id })}`),
    enabled: activo != null,
  });
  useEffect(() => {
    if (fotosExistentes) setFotos(fotosExistentes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fotosExistentes]);

  // Id del activo ya creado en un intento anterior. Si solo falló la asignación de
  // persona, el reintento actualiza ese activo en vez de crear un duplicado.
  const activoCreadoId = useRef<string | null>(null);

  const guardar = useMutation({
    mutationFn: async () => {
      const cuerpo = {
        nombre: texto(nombre),
        tiposActivo,
        ubicacionId: referencia(ubicacionId),
        activoPadreId: referencia(activoPadreId),
        criticidad,
        estado,
        motivoInactividadId: estado === 'FUERA_DE_LINEA' ? referencia(motivoInactividadId) : null,
        descripcion: texto(descripcion),
        fabricanteId: referencia(fabricanteId),
        modeloId: referencia(modeloId),
        numeroSerie: texto(numeroSerie),
        codigoQr: texto(codigoQr),
        expiracionGarantia: expiracionGarantia === '' ? null : expiracionGarantia,
        atributos,
        adjuntos: fotos.map((f) => f.id),
      };
      let idActivo: string;
      if (activo) {
        await api.put<Activo>(`/activos/${activo.id}`, cuerpo);
        idActivo = activo.id;
      } else if (activoCreadoId.current) {
        // Reintento tras fallar la persona: el activo ya existe, se actualiza.
        await api.put<Activo>(`/activos/${activoCreadoId.current}`, cuerpo);
        idActivo = activoCreadoId.current;
      } else {
        const creado = await api.post<Activo>('/activos', cuerpo);
        activoCreadoId.current = creado.id;
        idActivo = creado.id;
      }
      await sincronizarPersona(idActivo);
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['activo-asignaciones'] });
      alGuardar();
    },
  });

  // Cambia la asignación vigente solo si la persona elegida es otra; «sin persona» la libera.
  async function sincronizarPersona(idActivo: string): Promise<void> {
    const actual = personaVigente?.personaId ?? '';
    if (personaId === actual) return;
    if (personaId === '') {
      await api.post(`/activos/${idActivo}/asignaciones/liberar`);
    } else {
      await api.post(`/activos/${idActivo}/asignaciones`, { personaId });
    }
  }

  // §5.4: sin motivo, el tiempo fuera de línea no se puede clasificar y el
  // reporte de disponibilidad pierde la mitad de su valor.
  const faltaMotivo = estado === 'FUERA_DE_LINEA' && motivoInactividadId === '';

  return (
    <Dialogo titulo={activo ? `Editar ${activo.nombre}` : 'Nuevo activo'} ancho={640} alCerrar={alCerrar}>
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}


      <SeccionFormulario titulo="Identificación">
      <div className="campo ancho-completo">
        <label htmlFor="activo-nombre">Nombre</label>
        <input
          id="activo-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={250}
          required
          placeholder="Ej.: Láser Soprano Ice — Consultorio 3"
        />
      </div>

      <div className="ancho-completo">
        <SubidaImagenes etiqueta="Fotos del activo" valor={fotos} alCambiar={setFotos} />
      </div>

      <SelectorBuscable
        id="activo-tipo"
        etiqueta="Tipo de activo"
        valor={tiposActivo[0] ?? ''}
        alCambiar={(v) => setTiposActivo(v ? [v] : [])}
        opciones={(tipos ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
        vacio="Ningún tipo coincide. Se crean en Catálogos."
      />

      {!mostrarResto ? (
        <p className="campo-nota ancho-completo">Elija el tipo de activo para ver los campos que pide.</p>
      ) : null}
      </SeccionFormulario>

      {mostrarResto ? (
        <>


      <SeccionFormulario titulo="Estado y criticidad">
      <div className="rejilla-campos">
        <SelectorLista
          id="activo-criticidad"
          buscador
          etiqueta="Criticidad"
          valor={criticidad}
          alCambiar={(v) => setCriticidad(v as Criticidad)}
          opciones={CRITICIDADES.map((c) => ({ id: c, nombre: ETIQUETA_CRITICIDAD[c] }))}
          requerido
        />

        <SelectorLista
          id="activo-estado"
          buscador
          etiqueta="Estado"
          valor={estado}
          alCambiar={(v) => setEstado(v as EstadoActivo)}
          opciones={ESTADOS_ACTIVO.map((e) => ({ id: e, nombre: ETIQUETA_ESTADO_ACTIVO[e] }))}
          requerido
        />
      </div>

      {estado === 'FUERA_DE_LINEA' ? (
        <SelectorCatalogo
          id="activo-motivo"
          buscador
          codigo="MOTIVO_INACTIVIDAD"
          etiqueta="Motivo de inactividad"
          valor={motivoInactividadId}
          alCambiar={setMotivoInactividadId}
          requerido
        />
      ) : null}
      </SeccionFormulario>

      <SeccionFormulario titulo="Ubicación y fabricante">
      <div className="rejilla-campos">
        <SelectorUbicacion
          id="activo-ubicacion"
          buscador
          valor={ubicacionId}
          alCambiar={setUbicacionId}
          ubicaciones={ubicaciones}
        />
        <SelectorCatalogo
          id="activo-fabricante"
          buscador
          codigo="FABRICANTE"
          etiqueta="Fabricante"
          valor={fabricanteId}
          alCambiar={(v) => {
            setFabricanteId(v);
            setModeloId('');
          }}
          alCrear={() => setCreandoFabricante(true)}
          textoCrear="+ Nuevo fabricante"
        />
        {/* §3.2: Modelo cuelga de Fabricante; ofrecerlo suelto deja entrar
            combinaciones que después nadie puede interpretar en el reporte. */}
        <SelectorCatalogo
          id="activo-modelo"
          buscador
          codigo="MODELO"
          etiqueta="Modelo"
          valor={modeloId}
          alCambiar={setModeloId}
          padreId={fabricanteId || undefined}
          habilitado={fabricanteId !== ''}
          textoVacio={fabricanteId === '' ? 'Elija primero un fabricante' : 'Sin definir'}
        />
      </div>

      {creandoFabricante ? (
        <NuevoFabricante
          existentes={(fabricantes ?? []).filter((f) => f.activo)}
          alCrear={(id) => {
            setFabricanteId(id);
            setModeloId('');
            setCreandoFabricante(false);
          }}
          alCerrar={() => setCreandoFabricante(false)}
        />
      ) : null}

      <SelectorActivo
        id="activo-padre"
        etiqueta="Pertenece al activo"
        valor={activoPadreId}
        alCambiar={setActivoPadreId}
        nombreActual={activo?.activoPadreNombre}
        excluir={activo?.id}
      />
      <SelectorPersona
        id="activo-persona"
        etiqueta="Persona que lo usa"
        valor={personaId}
        alCambiar={setPersonaId}
        nombreActual={personaVigente?.personaNombre}
      />
      {personaId !== '' ? (
        <button type="button" className="boton secundario pequeno" onClick={() => setPersonaId('')}>
          Quitar persona
        </button>
      ) : null}
      {anteriores.length > 0 ? (
        <p className="campo-nota ancho-completo">
          Anteriores: {anteriores.map((a) => `${a.personaNombre} (${formatearFecha(a.desde)} – ${formatearFecha(a.hasta)})`).join(' · ')}
        </p>
      ) : null}
      </SeccionFormulario>

      <SeccionFormulario titulo="Identificación técnica">
      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="activo-serie">N.º de serie</label>
          <input id="activo-serie" value={numeroSerie} onChange={(e) => setNumeroSerie(e.target.value)} maxLength={100} />
        </div>
        <div className="campo">
          <label htmlFor="activo-qr">Código QR</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="activo-qr"
              value={codigoQr}
              onChange={(e) => setCodigoQr(e.target.value)}
              maxLength={40}
              placeholder="Se genera solo si se deja vacío"
              style={{ flex: 1 }}
            />
            {hayCamara() ? (
              <button type="button" className="boton secundario pequeno" onClick={() => setEscaneandoQr(true)}>
                Escanear
              </button>
            ) : null}
          </div>
        </div>
        <SelectorFecha
          id="activo-garantia"
          etiqueta="Vence la garantía"
          valor={expiracionGarantia}
          alCambiar={setExpiracionGarantia}
        />
      </div>


      {escaneandoQr ? (
        <EscanerQr
          alCerrar={() => setEscaneandoQr(false)}
          alDetectar={(codigo) => {
            setCodigoQr(codigo);
            setEscaneandoQr(false);
          }}
        />
      ) : null}

      <div className="campo ancho-completo">
        <label htmlFor="activo-descripcion">Descripción</label>
        <textarea
          id="activo-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={4000}
        />
      </div>
      </SeccionFormulario>

      {faltaMotivo ? (
        <Aviso tono="atencion">
          Un activo fuera de línea necesita un motivo: es lo que permite separar la parada planificada
          de la avería en el reporte de disponibilidad.
        </Aviso>
      ) : null}

      {camposDelFormulario.size > 0 ? (
        <SeccionFormulario titulo="Campos del tipo">
        <div className="rejilla-campos">
          {[...camposDelFormulario.entries()].map(([campo, obligatorio]) => {
            const definicion = CAMPOS_ACTIVO[campo];
            const etiqueta = `${definicion.etiqueta}${obligatorio ? ' *' : ''}`;
            const valor = atributos[campo] ?? '';
            if (definicion.tipo === 'fecha') {
              return (
                <SelectorFecha
                  key={campo}
                  id={`activo-campo-${campo}`}
                  etiqueta={etiqueta}
                  valor={valor}
                  alCambiar={(v) => establecerAtributo(campo, v)}
                />
              );
            }
            if (definicion.tipo === 'si_no' || definicion.tipo === 'lista') {
              const opciones =
                definicion.tipo === 'si_no' ? [{ valor: 'si', etiqueta: 'Sí' }, { valor: 'no', etiqueta: 'No' }] : definicion.opciones.map((o) => ({ valor: o, etiqueta: o }));
              return (
                <SelectorLista
                  key={campo}
                  id={`activo-campo-${campo}`}
                  buscador
                  etiqueta={etiqueta}
                  valor={valor}
                  alCambiar={(v) => establecerAtributo(campo, v)}
                  opciones={opciones.map((o) => ({ id: o.valor, nombre: o.etiqueta }))}
                  textoVacio="Sin dato"
                />
              );
            }
            return (
              <div key={campo} className="campo">
                <label htmlFor={`activo-campo-${campo}`}>{etiqueta}</label>
                <input
                  id={`activo-campo-${campo}`}
                  value={valor}
                  onChange={(e) => establecerAtributo(campo, e.target.value)}
                  inputMode={definicion.tipo === 'numero' ? 'numeric' : undefined}
                  maxLength={300}
                />
              </div>
            );
          })}
        </div>
        </SeccionFormulario>
      ) : null}

        </>
      ) : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={texto(nombre) === '' || faltaMotivo}
        etiqueta={activo ? 'Guardar cambios' : 'Crear activo'}
      />
    </Dialogo>
  );
}

// ─── Ubicaciones (§4) ────────────────────────────────────────────────────────

interface Ubicacion {
  id: string;
  nombre: string;
  ubicacionPadreId: string | null;
  tipoUbicacionId: string | null;
  tipoUbicacionNombre: string | null;
  direccion: string | null;
  descripcion: string | null;
  codigoQr: string | null;
  gruposACargo: { id: string; nombre: string }[];
}

interface NodoConConteos extends NodoUbicacion {
  hijos: number;
  activos: number;
  fotoUrl: string | null;
}

/**
 * Una fila del árbol de ubicaciones, y sus propios espacios internos debajo
 * (recursivo, sin límite de profundidad): un espacio como «Almacén de sede»
 * puede a su vez tener sub-espacios, y debe poder expandirse igual que una
 * sede — antes la tabla solo soportaba dos niveles fijos (sede y espacio).
 */
function FilaUbicacionArbol({
  nodo,
  profundidad,
  porPadre,
  expandidas,
  alternar,
  hayFiltro,
  cumple,
  escribe,
  navegar,
  onAgregarEspacio,
  onEditar,
  onEliminar,
  registrarFilaRaiz,
}: {
  nodo: NodoConConteos;
  profundidad: number;
  porPadre: Map<string, NodoConConteos[]>;
  expandidas: Set<string>;
  alternar: (id: string) => void;
  hayFiltro: boolean;
  cumple: (n: NodoConConteos) => boolean;
  escribe: boolean;
  navegar: (ruta: string) => void;
  onAgregarEspacio: (padreId: string) => void;
  onEditar: (id: string) => void;
  onEliminar: (nodo: NodoConConteos) => void;
  /** Solo se usa en la fila raíz (profundidad 0): así `ListaUbicaciones`
   * sabe dónde está cada sede en la pantalla para decidir, por scroll, cuál
   * mostrar en `.barra-sede-fija`. */
  registrarFilaRaiz?: (id: string, el: HTMLTableRowElement | null) => void;
}): JSX.Element {
  const todosLosHijos = porPadre.get(nodo.id) ?? [];
  // Si la búsqueda coincide con el espacio mismo, se muestran todos sus
  // sub-espacios sin filtrar más: quien busca «Santa Anita» quiere ver la
  // sede completa, no solo los espacios que también digan «santa».
  const hijos = hayFiltro && !cumple(nodo) ? todosLosHijos.filter(cumple) : todosLosHijos;
  const abierta = hayFiltro ? true : expandidas.has(nodo.id);

  return (
    <>
      <tr
        ref={profundidad === 0 ? (el) => registrarFilaRaiz?.(nodo.id, el) : undefined}
        className={`fila-clicable${profundidad > 0 ? ' fila-anidada' : ''}`}
        onClick={() => navegar(`/ubicaciones/${nodo.id}`)}
      >
        <td onClick={(e) => e.stopPropagation()}>
          <MiniaturaImagenTabla url={nodo.fotoUrl} nombre={nodo.nombre} />
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: profundidad * 24 }}>
            {todosLosHijos.length > 0 ? (
              <button
                type="button"
                className={`caret-arbol${abierta ? ' abierto' : ''}`}
                aria-expanded={abierta}
                aria-label={abierta ? 'Colapsar' : 'Expandir'}
                onClick={(e) => {
                  e.stopPropagation();
                  alternar(nodo.id);
                }}
              >
                <svg
                  className="caret-arbol-punta"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            ) : (
              <span className="caret-arbol" aria-hidden="true" />
            )}
            <span style={{ fontWeight: profundidad === 0 ? 700 : 600 }}>{nodo.nombre}</span>
          </div>
        </td>
        <td>{nodo.hijos || '—'}</td>
        <td>{nodo.activos || '—'}</td>
        <td>
          {escribe ? (
            <div className="acciones-fila" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="boton-icono"
                title="Añadir espacio"
                aria-label="Añadir espacio"
                onClick={() => onAgregarEspacio(nodo.id)}
              >
                <IconoMas />
              </button>
              <button type="button" className="boton-icono" title="Editar" aria-label="Editar" onClick={() => onEditar(nodo.id)}>
                <IconoEditar />
              </button>
              <button
                type="button"
                className="boton-icono peligro"
                title="Eliminar"
                aria-label="Eliminar"
                onClick={() => onEliminar(nodo)}
              >
                <IconoEliminar />
              </button>
            </div>
          ) : null}
        </td>
      </tr>

      {abierta
        ? hijos.map((hijo) => (
            <FilaUbicacionArbol
              key={hijo.id}
              nodo={hijo}
              profundidad={profundidad + 1}
              porPadre={porPadre}
              expandidas={expandidas}
              alternar={alternar}
              hayFiltro={hayFiltro}
              cumple={cumple}
              escribe={escribe}
              navegar={navegar}
              onAgregarEspacio={onAgregarEspacio}
              onEditar={onEditar}
              onEliminar={onEliminar}
            />
          ))
        : null}
    </>
  );
}

/** Igual que `FilaUbicacionArbol`, pero como tarjeta — para el árbol de ubicaciones en móvil. */
function TarjetaUbicacionArbol({
  nodo,
  profundidad,
  porPadre,
  expandidas,
  alternar,
  hayFiltro,
  cumple,
  escribe,
  navegar,
  onAgregarEspacio,
  onEditar,
  onEliminar,
}: {
  nodo: NodoConConteos;
  profundidad: number;
  porPadre: Map<string, NodoConConteos[]>;
  expandidas: Set<string>;
  alternar: (id: string) => void;
  hayFiltro: boolean;
  cumple: (n: NodoConConteos) => boolean;
  escribe: boolean;
  navegar: (ruta: string) => void;
  onAgregarEspacio: (padreId: string) => void;
  onEditar: (id: string) => void;
  onEliminar: (nodo: NodoConConteos) => void;
}): JSX.Element {
  const todosLosHijos = porPadre.get(nodo.id) ?? [];
  const hijos = hayFiltro && !cumple(nodo) ? todosLosHijos.filter(cumple) : todosLosHijos;
  const abierta = hayFiltro ? true : expandidas.has(nodo.id);

  return (
    <>
      <div
        className="tarjeta-ot"
        style={profundidad > 0 ? { marginLeft: profundidad * 16 } : undefined}
        role="button"
        tabIndex={0}
        onClick={() => navegar(`/ubicaciones/${nodo.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') navegar(`/ubicaciones/${nodo.id}`);
        }}
      >
        {todosLosHijos.length > 0 ? (
          <button
            type="button"
            className={`caret-arbol${abierta ? ' abierto' : ''}`}
            aria-expanded={abierta}
            aria-label={abierta ? 'Colapsar' : 'Expandir'}
            onClick={(e) => {
              e.stopPropagation();
              alternar(nodo.id);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        ) : null}
        <MiniaturaImagenTabla url={nodo.fotoUrl} nombre={nodo.nombre} />
        <div className="tarjeta-ot-cuerpo">
          <div className="tarjeta-ot-titulo">{nodo.nombre}</div>
          <div className="tarjeta-ot-datos">
            <span>{nodo.hijos || 0} espacio(s)</span>
            <span>{nodo.activos || 0} activo(s)</span>
          </div>
        </div>
        {escribe ? (
          <div className="acciones-fila" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="boton-icono" title="Añadir espacio" aria-label="Añadir espacio" onClick={() => onAgregarEspacio(nodo.id)}>
              <IconoMas />
            </button>
            <button type="button" className="boton-icono" title="Editar" aria-label="Editar" onClick={() => onEditar(nodo.id)}>
              <IconoEditar />
            </button>
            <button type="button" className="boton-icono peligro" title="Eliminar" aria-label="Eliminar" onClick={() => onEliminar(nodo)}>
              <IconoEliminar />
            </button>
          </div>
        ) : null}
      </div>

      {abierta
        ? hijos.map((hijo) => (
            <TarjetaUbicacionArbol
              key={hijo.id}
              nodo={hijo}
              profundidad={profundidad + 1}
              porPadre={porPadre}
              expandidas={expandidas}
              alternar={alternar}
              hayFiltro={hayFiltro}
              cumple={cumple}
              escribe={escribe}
              navegar={navegar}
              onAgregarEspacio={onAgregarEspacio}
              onEditar={onEditar}
              onEliminar={onEliminar}
            />
          ))
        : null}
    </>
  );
}

export function ListaUbicaciones(): JSX.Element {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [editando, setEditando] = useState<{ ubicacion: Ubicacion | null; padreId: string | null } | null>(null);
  const [eliminando, setEliminando] = useState<NodoConConteos | null>(null);
  const [buscar, setBuscar] = useState('');
  const [soloConActivos, setSoloConActivos] = useState(false);

  // Qué sede mostrar en `.barra-sede-fija` mientras se scrollea por sus
  // espacios. `position: fixed` calculado a mano por JS, no `sticky`: con
  // varias sedes seguidas, cada una con su propio sticky, se solapaban en
  // vez de reemplazarse (§ bug reportado, dos intentos con sticky fallaron).
  // Se activa recién cuando la fila de la sede sale entera de la vista (no
  // apenas cruza el borde) y se desactiva al terminar su sección — con el
  // efecto "push" de iOS al llegar la siguiente.
  const [sedeFijaId, setSedeFijaId] = useState<string | null>(null);
  const filasRaizRef = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const tablaContenedorRef = useRef<HTMLDivElement>(null);
  const barraRef = useRef<HTMLDivElement>(null);
  const raicesRef = useRef<NodoConConteos[]>([]);

  const escribe = puede('ubicacion:escribir');

  const { data, isLoading } = useQuery({
    queryKey: ['ubicaciones-arbol'],
    queryFn: () => api.get<NodoConConteos[]>('/ubicaciones/arbol'),
  });

  const refrescar = async (): Promise<void> => {
    await cliente.invalidateQueries({ queryKey: ['ubicaciones-arbol'] });
    await cliente.invalidateQueries({ queryKey: ['ubicaciones'] });
  };

  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/ubicaciones/${id}`),
    onSuccess: async () => {
      await refrescar();
      setEliminando(null);
    },
  });

  // La ficha completa solo se pide al abrir el formulario: el árbol no trae
  // dirección ni grupos a cargo, y precargarlos para todos sería un despilfarro.
  const abrirEdicion = async (id: string): Promise<void> => {
    const ubicacion = await cliente.fetchQuery({
      queryKey: ['ubicacion', id],
      queryFn: () => api.get<Ubicacion>(`/ubicaciones/${id}`),
    });
    setEditando({ ubicacion, padreId: ubicacion.ubicacionPadreId });
  };

  // La ficha de detalle (solo lectura) enlaza acá con `?editar=<id>` para
  // abrir el formulario de edición sin duplicarlo: se consume una sola vez
  // y se limpia de la URL para no reabrirse en cada refresco.
  useEffect(() => {
    const idEditar = parametros.get('editar');
    if (!idEditar || !escribe) return;
    setParametros((p) => {
      p.delete('editar');
      return p;
    }, { replace: true });
    void abrirEdicion(idEditar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametros, escribe]);

  const todasLasRaices = (data ?? []).filter((u) => u.padreId === null);
  // Mapa de padre → hijos directos, a cualquier profundidad: un espacio interno
  // (ej. «Almacén de sede» dentro de «Chorrillos») puede a su vez tener sus
  // propios sub-espacios, así que la tabla no puede quedarse en dos niveles.
  const porPadre = new Map<string, NodoConConteos[]>();
  for (const nodo of data ?? []) {
    if (!nodo.padreId) continue;
    porPadre.set(nodo.padreId, [...(porPadre.get(nodo.padreId) ?? []), nodo]);
  }

  const filtroTexto = buscar.trim().toLowerCase();
  const cumple = (n: NodoConConteos): boolean =>
    (!filtroTexto || n.nombre.toLowerCase().includes(filtroTexto)) && (!soloConActivos || n.activos > 0);

  // Recorre todos los descendientes (no solo los hijos directos): un espacio
  // permanece en la lista filtrada si él, o cualquier descendiente a cualquier
  // profundidad, cumple — no tendría sentido esconder «San Isidro» solo porque
  // el filtro coincide con un consultorio que cuelga de uno de sus espacios.
  const algunDescendienteCumple = (id: string): boolean =>
    (porPadre.get(id) ?? []).some((hijo) => cumple(hijo) || algunDescendienteCumple(hijo.id));

  const hayFiltro = filtroTexto !== '' || soloConActivos;
  const raices = hayFiltro
    ? todasLasRaices.filter((raiz) => cumple(raiz) || algunDescendienteCumple(raiz.id))
    : todasLasRaices;
  raicesRef.current = raices;
  const sedeFija = raices.find((r) => r.id === sedeFijaId) ?? null;
  const hayTabla = !esMovil && raices.length > 0;

  const alternar = (id: string): void =>
    setExpandidas((previas) => {
      const siguientes = new Set(previas);
      if (siguientes.has(id)) siguientes.delete(id);
      else siguientes.add(id);
      return siguientes;
    });

  const expandirTodo = (): void => setExpandidas(new Set((data ?? []).map((n) => n.id)));
  const colapsarTodo = (): void => setExpandidas(new Set());

  // Solo lee refs del DOM: estable entre renders, no depende de `raices`
  // directamente (usa `raicesRef`) para no tener que recrearse en cada uno.
  const recalcularSedeFija = useCallback((): void => {
    const tabla = tablaContenedorRef.current;
    const barra = barraRef.current;
    const contenedor = tabla?.closest<HTMLElement>('.contenido');
    if (!tabla || !barra || !contenedor) return;

    // Referencia real: el borde superior de `.contenido`, no `0` — la barra
    // superior fija del layout ya ocupa esos primeros píxeles del viewport.
    const limite = contenedor.getBoundingClientRect().top;
    const rectTabla = tabla.getBoundingClientRect();
    const alto = barra.offsetHeight;
    const lista = raicesRef.current;

    let actual: string | null = null;
    let desplazamiento = 0;

    for (let i = 0; i < lista.length; i++) {
      const fila = filasRaizRef.current.get(lista[i]!.id);
      if (!fila) continue;
      // Apenas la fila de la sede empieza a taparse (su borde superior
      // cruza el límite) ya se fija la barra — no hace falta esperar a que
      // desaparezca del todo (§ pedido).
      if (fila.getBoundingClientRect().top > limite) break;
      // Fin de su sección = donde arranca la siguiente sede, o el final de
      // la tabla si es la última.
      const siguiente = lista[i + 1] ? filasRaizRef.current.get(lista[i + 1]!.id) : undefined;
      const fin = siguiente ? siguiente.getBoundingClientRect().top : rectTabla.bottom;
      if (fin <= limite) continue; // su sección ya pasó entera
      actual = lista[i]!.id;
      // Al llegar la siguiente sede, empuja a la barra actual hacia arriba
      // en vez de que ambas se superpongan (efecto "push", como iOS).
      desplazamiento = Math.min(0, fin - limite - alto);
      break;
    }

    barra.style.top = `${limite}px`;
    barra.style.left = `${rectTabla.left}px`;
    barra.style.width = `${rectTabla.width}px`;
    barra.style.transform = `translateY(${desplazamiento}px)`;
    barra.style.clipPath = `inset(${-desplazamiento}px 0 0 0)`;
    barra.classList.toggle('visible', actual !== null);
    setSedeFijaId((previa) => (previa === actual ? previa : actual));
  }, []);

  useEffect(() => {
    if (!hayTabla) return;
    const contenedor = tablaContenedorRef.current?.closest<HTMLElement>('.contenido');
    if (!contenedor) return;
    contenedor.addEventListener('scroll', recalcularSedeFija, { passive: true });
    window.addEventListener('resize', recalcularSedeFija);
    return () => {
      contenedor.removeEventListener('scroll', recalcularSedeFija);
      window.removeEventListener('resize', recalcularSedeFija);
    };
  }, [hayTabla, recalcularSedeFija]);

  // Tras cada render (expandir/colapsar, filtro, datos nuevos) las
  // posiciones de las filas cambian. No entra en bucle: el `setState` de
  // adentro solo dispara un re-render si la sede activa realmente cambió.
  useLayoutEffect(() => {
    recalcularSedeFija();
  });

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Ubicaciones</h1>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar ubicaciones"
            placeholder="Buscar Ubicaciones"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </div>
        {escribe ? (
          <>
            <BotonImportarCsv ruta="ubicaciones" alTerminar={() => void refrescar()} />
            <button
              type="button"
              className="boton oculto-en-movil"
              onClick={() => setEditando({ ubicacion: null, padreId: null })}
            >
              + Nueva Ubicación
            </button>
            <button
              type="button"
              className="boton-flotante-crear"
              aria-label="Nueva ubicación"
              onClick={() => setEditando({ ubicacion: null, padreId: null })}
            >
              <IconoMas />
            </button>
          </>
        ) : null}
      </div>

      <div className="rejilla-indicadores">
        <Indicador titulo="UBICACIONES RAÍZ" valor={todasLasRaices.length} tono="azul" />
        <Indicador titulo="SUB-UBICACIONES" valor={(data?.length ?? 0) - todasLasRaices.length} tono="gris" />
        <Indicador
          titulo="ACTIVOS LOCALIZADOS"
          valor={(data ?? []).reduce((t, u) => t + u.activos, 0)}
          tono="verde"
          className="col-ocultar-movil"
        />
      </div>

      <div className="filtros filtros-escritorio">
        <label className="casilla">
          <input type="checkbox" checked={soloConActivos} onChange={(e) => setSoloConActivos(e.target.checked)} />
          Solo con activos
        </label>
        <button type="button" className="boton secundario pequeno" onClick={expandirTodo}>
          Expandir todo
        </button>
        <button type="button" className="boton secundario pequeno" onClick={colapsarTodo}>
          Colapsar todo
        </button>
      </div>

      <div className="panel">
        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? (
          <Vacio
            titulo="Sin ubicaciones"
            detalle={escribe ? 'Cree la primera sede con «Nueva ubicación».' : undefined}
          />
        ) : null}

        {data && data.length > 0 && raices.length === 0 ? (
          <Vacio titulo="Nada coincide con el filtro" detalle="Prueba con otro término de búsqueda." />
        ) : null}

        {raices.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {raices.map((raiz) => (
                <TarjetaUbicacionArbol
                  key={raiz.id}
                  nodo={raiz}
                  profundidad={0}
                  porPadre={porPadre}
                  expandidas={expandidas}
                  alternar={alternar}
                  hayFiltro={hayFiltro}
                  cumple={cumple}
                  escribe={escribe}
                  navegar={navegar}
                  onAgregarEspacio={(padreId) => setEditando({ ubicacion: null, padreId })}
                  onEditar={(id) => void abrirEdicion(id)}
                  onEliminar={setEliminando}
                />
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor-vertical" ref={tablaContenedorRef}>
              {/* Siempre montada (visibilidad la controla `visibility`, no un
                  `if`): así su posición y tamaño (`barraRef.current`) están
                  disponibles para medir apenas hay scroll, sin esperar a un
                  primer render condicional. Posición en `fixed`, calculada a
                  mano en `recalcularSedeFija` — no `sticky`: con varias sedes
                  seguidas, cada una pegajosa, se solapaban en vez de
                  reemplazarse limpio (§ bug reportado, dos intentos con
                  sticky fallaron antes de este). */}
              <div
                ref={barraRef}
                className="barra-sede-fija"
                role={sedeFija ? 'button' : undefined}
                tabIndex={sedeFija ? 0 : undefined}
                onClick={() => {
                  if (sedeFija) filasRaizRef.current.get(sedeFija.id)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }}
              >
                {sedeFija ? (
                  <>
                    <MiniaturaImagenTabla url={sedeFija.fotoUrl} nombre={sedeFija.nombre} />
                    <span>{sedeFija.nombre}</span>
                    <span className="barra-sede-fija-conteo">
                      {sedeFija.hijos || 0} espacios · {sedeFija.activos || 0} activos
                    </span>
                  </>
                ) : null}
              </div>
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Foto</th>
                    <th>Nombre</th>
                    <th>Espacios</th>
                    <th>Activos</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {raices.map((raiz) => (
                    <FilaUbicacionArbol
                      key={raiz.id}
                      nodo={raiz}
                      profundidad={0}
                      porPadre={porPadre}
                      expandidas={expandidas}
                      alternar={alternar}
                      hayFiltro={hayFiltro}
                      cumple={cumple}
                      escribe={escribe}
                      navegar={navegar}
                      onAgregarEspacio={(padreId) => setEditando({ ubicacion: null, padreId })}
                      onEditar={(id) => void abrirEdicion(id)}
                      onEliminar={setEliminando}
                      registrarFilaRaiz={(id, el) => {
                        if (el) filasRaizRef.current.set(id, el);
                        else filasRaizRef.current.delete(id);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      {editando ? (
        <FormularioUbicacion
          ubicacion={editando.ubicacion}
          padreInicial={editando.padreId}
          alCerrar={() => setEditando(null)}
          alGuardar={async () => {
            await refrescar();
            setEditando(null);
          }}
        />
      ) : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar ubicación"
          mensaje={`Se eliminará «${eliminando.nombre}». Si tiene sub-ubicaciones o activos asociados, el servidor lo impedirá.`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(null);
          }}
          alConfirmar={() => eliminar.mutate(eliminando.id)}
        />
      ) : null}
    </>
  );
}

function FormularioUbicacion({
  ubicacion,
  padreInicial,
  alCerrar,
  alGuardar,
}: {
  ubicacion: Ubicacion | null;
  padreInicial: string | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(ubicacion?.nombre ?? '');
  const [ubicacionPadreId, setUbicacionPadreId] = useState(ubicacion?.ubicacionPadreId ?? padreInicial ?? '');
  const [tipoUbicacionId, setTipoUbicacionId] = useState(ubicacion?.tipoUbicacionId ?? '');
  const [direccion, setDireccion] = useState(ubicacion?.direccion ?? '');
  const [descripcion, setDescripcion] = useState(ubicacion?.descripcion ?? '');
  // Vacío = el backend genera uno al crear. Con valor = se vincula una etiqueta
  // QR ya impresa (se teclea o se escanea).
  const [codigoQr, setCodigoQr] = useState(ubicacion?.codigoQr ?? '');
  const [escaneandoQr, setEscaneandoQr] = useState(false);
  const [gruposACargo, setGruposACargo] = useState<string[]>(ubicacion?.gruposACargo.map((g) => g.id) ?? []);
  const [fotos, setFotos] = useState<AdjuntoImagen[]>([]);

  const { puede } = useSesion();
  const { data: ubicaciones } = useUbicaciones();
  const { data: tiposUbicacion } = useTiposUbicacion();
  const { data: grupos } = useGruposOpciones(puede('grupo:leer'));

  const { data: fotosExistentes } = useQuery({
    queryKey: ['adjuntos', 'UBICACION', ubicacion?.id],
    queryFn: () => api.get<AdjuntoImagen[]>(`/adjuntos${consulta({ entidad: 'UBICACION', entidadId: ubicacion?.id })}`),
    enabled: ubicacion != null,
  });
  useEffect(() => {
    if (fotosExistentes) setFotos(fotosExistentes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fotosExistentes]);

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        nombre: texto(nombre),
        ubicacionPadreId: referencia(ubicacionPadreId),
        tipoUbicacionId: referencia(tipoUbicacionId),
        direccion: texto(direccion),
        descripcion: texto(descripcion),
        codigoQr: texto(codigoQr),
        gruposACargo,
        adjuntos: fotos.map((f) => f.id),
      };
      return ubicacion
        ? api.put<Ubicacion>(`/ubicaciones/${ubicacion.id}`, cuerpo)
        : api.post<Ubicacion>('/ubicaciones', cuerpo);
    },
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={ubicacion ? `Editar ${ubicacion.nombre}` : 'Nueva ubicación'} ancho={560} alCerrar={alCerrar}>
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="ubicacion-nombre">Nombre</label>
        <input
          id="ubicacion-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={200}
          required
          placeholder="Ej.: Santa Anita — Consultorio 3"
        />
      </div>

      <SelectorUbicacion
        id="ubicacion-padre"
        etiqueta="Pertenece a"
        valor={ubicacionPadreId}
        alCambiar={setUbicacionPadreId}
        excluir={ubicacion?.id}
        ubicaciones={ubicaciones}
      />

      <SelectorLista
        id="ubicacion-tipo"
        etiqueta="Tipo de ubicación"
        valor={tipoUbicacionId}
        alCambiar={setTipoUbicacionId}
        opciones={(tiposUbicacion ?? []).map((t) => ({ id: t.id, nombre: t.nombre }))}
        textoVacio="Sin tipo"
      />

      <div className="campo">
        <label htmlFor="ubicacion-direccion">Dirección</label>
        <input
          id="ubicacion-direccion"
          value={direccion}
          onChange={(e) => setDireccion(e.target.value)}
          maxLength={300}
          placeholder={ubicacionPadreId ? 'Si se deja vacía, se hereda de la sede' : ''}
        />
      </div>

      <div className="campo">
        <label htmlFor="ubicacion-qr">Código QR</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            id="ubicacion-qr"
            value={codigoQr}
            onChange={(e) => setCodigoQr(e.target.value)}
            maxLength={40}
            placeholder="Se genera solo si se deja vacío"
            style={{ flex: 1 }}
          />
          {hayCamara() ? (
            <button type="button" className="boton secundario pequeno" onClick={() => setEscaneandoQr(true)}>
              Escanear
            </button>
          ) : null}
        </div>
      </div>

      {escaneandoQr ? (
        <EscanerQr
          alCerrar={() => setEscaneandoQr(false)}
          alDetectar={(codigo) => {
            setCodigoQr(codigo);
            setEscaneandoQr(false);
          }}
        />
      ) : null}

      {puede('grupo:leer') ? (
        <SeleccionMultiple
          etiqueta="Grupos a cargo"
          opciones={(grupos ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
          seleccion={gruposACargo}
          alCambiar={setGruposACargo}
          vacio="Aún no hay grupos. Se crean en Grupos y Usuarios."
        />
      ) : null}

      <div className="campo">
        <label htmlFor="ubicacion-descripcion">Descripción</label>
        <textarea
          id="ubicacion-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={4000}
        />
      </div>

      <SubidaImagenes etiqueta="Fotos de la ubicación" valor={fotos} alCambiar={setFotos} />

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={texto(nombre) === ''}
        etiqueta={ubicacion ? 'Guardar cambios' : 'Crear ubicación'}
      />
    </Dialogo>
  );
}

// ─── Tipos de falla (§7) ─────────────────────────────────────────────────────

interface TipoFalla {
  id: string;
  nombre: string;
  descripcion: string | null;
  familiaId: string | null;
  familia: string | null;
  activo: boolean;
  ordenes: number;
}

export function ListaTiposFalla(): JSX.Element {
  const { puede } = useSesion();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<TipoFalla | null | undefined>(undefined);
  const [desactivando, setDesactivando] = useState<TipoFalla | null>(null);
  const [buscar, setBuscar] = useState('');

  const escribe = puede('tipo_falla:escribir');

  // Quien administra la taxonomía ve también las categorías retiradas: si no,
  // retirar una por error sería irreversible desde la interfaz.
  const { data, isLoading } = useQuery({
    queryKey: ['tipos-falla', escribe ? 'con-retiradas' : 'vigentes'],
    queryFn: () => api.get<TipoFalla[]>(`/tipos-falla${consulta({ incluirInactivos: escribe || '' })}`),
  });

  const desactivar = useMutation({
    mutationFn: (id: string) => api.delete(`/tipos-falla/${id}`),
    onSuccess: async () => {
      await cliente.invalidateQueries({ queryKey: ['tipos-falla'] });
      setDesactivando(null);
    },
  });

  // El buscador filtra por nombre o familia: con decenas de categorías,
  // desplazarse familia por familia para encontrar una es más lento que
  // escribir las primeras letras.
  const filtroTexto = buscar.trim().toLowerCase();
  const datosFiltrados = (data ?? []).filter(
    (t) =>
      !filtroTexto ||
      t.nombre.toLowerCase().includes(filtroTexto) ||
      (t.familia ?? '').toLowerCase().includes(filtroTexto),
  );

  const familias = new Map<string, TipoFalla[]>();
  for (const tipo of datosFiltrados) {
    const clave = tipo.familia ?? 'Sin familia';
    familias.set(clave, [...(familias.get(clave) ?? []), tipo]);
  }

  return (
    <>
      <div className="cabecera-pagina cabecera-lista">
        <div className="cabecera-lista-titulo">
          <h1>Tipos de Fallas</h1>
          <p className="cabecera-subtitulo">Taxonomía normalizada de síntomas: la dimensión que responde qué falla más.</p>
        </div>
        <div className="campo cabecera-lista-buscar">
          <IconoLupa />
          <input
            aria-label="Buscar tipos de falla"
            placeholder="Buscar por nombre o familia…"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
        </div>
        {escribe ? (
          <>
            <button type="button" className="boton oculto-en-movil" onClick={() => setEditando(null)}>
              Nuevo tipo de falla
            </button>
            <button
              type="button"
              className="boton-flotante-crear"
              aria-label="Nuevo tipo de falla"
              onClick={() => setEditando(null)}
            >
              <IconoMas />
            </button>
          </>
        ) : null}
      </div>

      {/* §7.4: la familia es un campo, no un prefijo del nombre. Es lo que
          impide que «AC - No enciende» y «AC- No Enciende» vuelvan a dividir
          el mismo síntoma en dos filas del reporte. */}
      <Aviso tono="info">
        La familia es un campo propio y la unicidad ignora mayúsculas y acentos, de modo que un
        duplicado no puede volver a entrar al catálogo.
      </Aviso>

      <div className="rejilla-indicadores">
        <Indicador titulo="CATEGORÍAS" valor={data?.length ?? '—'} />
        <Indicador titulo="FAMILIAS" valor={familias.size} />
        <Indicador
          titulo="USOS EN ÓRDENES"
          valor={(data ?? []).reduce((t, f) => t + f.ordenes, 0)}
          className="col-ocultar-movil"
        />
      </div>

      {isLoading ? <Cargando /> : null}
      {data && data.length === 0 ? (
        <div className="panel">
          <Vacio
            titulo="Sin tipos de falla"
            detalle={escribe ? 'Cree el primero con «Nuevo tipo de falla».' : undefined}
          />
        </div>
      ) : null}
      {data && data.length > 0 && datosFiltrados.length === 0 ? (
        <div className="panel">
          <Vacio titulo="Sin resultados" detalle="Ningún tipo de falla coincide con la búsqueda." />
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {[...familias.entries()].map(([familia, tipos]) => (
          <section className="panel" key={familia}>
            <div className="panel-cabecera">
              <h2>{familia}</h2>
              <span className="nav-contador">{tipos.length}</span>
            </div>
            {esMovil ? (
              <div className="tarjetas-ot">
                {tipos.map((t) => (
                  <div key={t.id} className="tarjeta-ot" style={t.activo ? undefined : { opacity: 0.55 }}>
                    <div className="tarjeta-ot-cuerpo">
                      <div className="tarjeta-ot-cabecera">
                        <div className="tarjeta-ot-titulo">
                          {t.nombre}
                          {t.activo ? null : <Etiqueta tono="gris">Retirado</Etiqueta>}
                        </div>
                      </div>
                      {t.descripcion ? <div className="tarjeta-ot-nota">{t.descripcion}</div> : null}
                      <div className="tarjeta-ot-datos">
                        <span>{t.ordenes} orden(es)</span>
                      </div>
                    </div>
                    {escribe ? (
                      <div className="acciones-fila">
                        <button type="button" className="boton secundario pequeno" onClick={() => setEditando(t)}>
                          Editar
                        </button>
                        {t.activo ? (
                          <button
                            type="button"
                            className="boton secundario pequeno"
                            onClick={() => setDesactivando(t)}
                          >
                            Retirar
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="tabla-contenedor">
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th>Criterio de aplicación</th>
                      <th>Órdenes</th>
                      {escribe ? <th /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {tipos.map((t) => (
                      <tr key={t.id} style={t.activo ? undefined : { opacity: 0.55 }}>
                        <td style={{ fontWeight: 600 }}>
                          {t.nombre}
                          {t.activo ? null : <Etiqueta tono="gris">Retirado</Etiqueta>}
                        </td>
                        <td style={{ color: 'var(--texto-medio)' }}>{t.descripcion ?? '—'}</td>
                        <td className="numero-registro">{t.ordenes}</td>
                        {escribe ? (
                          <td>
                            <div className="acciones-fila">
                              <button type="button" className="boton secundario pequeno" onClick={() => setEditando(t)}>
                                Editar
                              </button>
                              {t.activo ? (
                                <button
                                  type="button"
                                  className="boton secundario pequeno"
                                  onClick={() => setDesactivando(t)}
                                >
                                  Retirar
                                </button>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ))}
      </div>

      {editando !== undefined ? (
        <FormularioTipoFalla
          tipo={editando}
          alCerrar={() => setEditando(undefined)}
          alGuardar={async () => {
            await cliente.invalidateQueries({ queryKey: ['tipos-falla'] });
            setEditando(undefined);
          }}
        />
      ) : null}

      {desactivando ? (
        <DialogoConfirmar
          titulo="Retirar tipo de falla"
          etiqueta="Retirar"
          mensaje={`«${desactivando.nombre}» dejará de ofrecerse en órdenes nuevas. Las ${desactivando.ordenes} orden(es) que ya lo usan conservan su clasificación.`}
          cargando={desactivar.isPending}
          error={desactivar.error}
          alCerrar={() => {
            desactivar.reset();
            setDesactivando(null);
          }}
          alConfirmar={() => desactivar.mutate(desactivando.id)}
        />
      ) : null}
    </>
  );
}

function FormularioTipoFalla({
  tipo,
  alCerrar,
  alGuardar,
}: {
  tipo: TipoFalla | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(tipo?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(tipo?.descripcion ?? '');
  const [familiaId, setFamiliaId] = useState(tipo?.familiaId ?? '');
  const [activo, setActivo] = useState(tipo?.activo ?? true);
  // §7.3: el servidor avisa de un nombre parecido antes de dejar entrar otro
  // duplicado; el alta solo continúa si quien la hace confirma que no lo es.
  const [confirmarSimilar, setConfirmarSimilar] = useState(false);

  const guardar = useMutation({
    mutationFn: (confirmado: boolean) => {
      const cuerpo = {
        nombre: texto(nombre),
        descripcion: texto(descripcion),
        familiaId: referencia(familiaId),
        activo,
        confirmarSimilar: confirmado,
      };
      return tipo
        ? api.put<unknown>(`/tipos-falla/${tipo.id}`, cuerpo)
        : api.post<unknown>('/tipos-falla', cuerpo);
    },
    onSuccess: alGuardar,
    onError: (error: unknown) => {
      if (error instanceof ErrorApi && error.codigo === CODIGOS_ERROR.TIPO_FALLA_SIMILAR) {
        setConfirmarSimilar(true);
      }
    },
  });

  const similar =
    guardar.error instanceof ErrorApi && guardar.error.codigo === CODIGOS_ERROR.TIPO_FALLA_SIMILAR;

  return (
    <Dialogo titulo={tipo ? `Editar ${tipo.nombre}` : 'Nuevo tipo de falla'} ancho={520} alCerrar={alCerrar}>
      {guardar.isError && !similar ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}
      {similar ? <Aviso tono="atencion">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="falla-nombre">Categoría</label>
        <input
          id="falla-nombre"
          value={nombre}
          onChange={(e) => {
            setNombre(e.target.value);
            setConfirmarSimilar(false);
            guardar.reset();
          }}
          maxLength={200}
          required
          placeholder="Ej.: No enciende"
        />
      </div>

      <SelectorCatalogo
        id="falla-familia"
        codigo="FAMILIA_FALLA"
        etiqueta="Familia"
        valor={familiaId}
        alCambiar={setFamiliaId}
      />

      <div className="campo">
        <label htmlFor="falla-descripcion">Criterio de aplicación</label>
        <textarea
          id="falla-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={2000}
          placeholder="Cuándo corresponde usar esta categoría y cuándo no."
        />
      </div>

      {tipo ? (
        <label className="casilla" style={{ marginBottom: 14 }}>
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          <span>Disponible para clasificar órdenes nuevas</span>
        </label>
      ) : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate(confirmarSimilar)}
        cargando={guardar.isPending}
        deshabilitado={texto(nombre) === ''}
        etiqueta={confirmarSimilar ? 'Crear de todos modos' : tipo ? 'Guardar cambios' : 'Crear tipo de falla'}
      />
    </Dialogo>
  );
}

// ─── Usuarios y grupos (§12) ─────────────────────────────────────────────────

interface Usuario {
  id: string;
  nombreCompleto: string;
  email: string | null;
  telefonoMovil: string | null;
  tipoAcceso: TipoAcceso;
  estadoInvitacion: string;
  ubicacionAsignadaId: string | null;
  ubicacionAsignada: string | null;
  grupos: { id: string; nombre: string }[];
  ultimaVisita: string | null;
  activo: boolean;
  consumeAsiento: boolean;
  esCompartido: boolean;
}

interface Grupo {
  id: string;
  nombre: string;
  descripcion: string | null;
  tipo: TipoGrupo;
  accesoMateriales: boolean;
  administradorId: string | null;
  administrador: string | null;
  miembros: { id: string; nombreCompleto: string }[];
  ordenesAbiertas: number;
  tiposActivo: { id: string; nombre: string }[];
  procedimientos: { id: string; nombre: string }[];
}

export function ListaUsuarios(): JSX.Element {
  const { puede, usuario } = useSesion();
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [pagina, setPagina] = useState(1);
  const [editandoUsuario, setEditandoUsuario] = useState<Usuario | null | undefined>(undefined);
  const [editandoGrupo, setEditandoGrupo] = useState<Grupo | null | undefined>(undefined);
  const [desactivando, setDesactivando] = useState<Usuario | null>(null);
  const [tabletsDe, setTabletsDe] = useState<Usuario | null>(null);

  const administraGrupos = puede('grupo:administrar');

  /**
   * Organización ya migrada al núcleo: las altas se hacen en el portal Continnua y llegan aquí por
   * el webhook `membership.changed` (`continnua_plataforma/docs/adr/ADR-007`). El backend rechaza
   * `POST /usuarios` en ese estado, así que ofrecer el formulario sería ofrecer un error.
   */
  const portalUsuarios = usuario?.gestionUsuariosEnNucleo ?? null;
  const administraUsuarios = puede('usuario:administrar');
  /**
   * Solo el **alta** se va al portal. Editar y desactivar siguen aquí: la ubicación asignada y los
   * grupos de trabajo son dominio de este módulo y el núcleo no los conoce.
   */
  const invitaAqui = administraUsuarios && portalUsuarios === null;

  const { data: usuarios, isLoading } = useQuery({
    queryKey: ['usuarios', pagina],
    queryFn: () => api.get<Pagina<Usuario>>(`/usuarios${consulta({ pagina, tamano: 25 })}`),
  });

  // Clave propia («grupos-completo», no «grupos»): `useGruposOpciones` ya usa
  // `['grupos']` para una forma distinta (miembros como conteo, no como
  // array) — compartir la clave con esta mezclaba ambas formas en el mismo
  // caché y hacía tronar esta tabla con `g.miembros.map is not a function`
  // en cuanto se abría el diálogo de grupo (§ bug reportado).
  const { data: grupos } = useQuery({
    queryKey: ['grupos-completo'],
    queryFn: () => api.get<Grupo[]>('/grupos'),
  });

  const refrescar = async (): Promise<void> => {
    await cliente.invalidateQueries({ queryKey: ['usuarios'] });
    await cliente.invalidateQueries({ queryKey: ['usuarios-opciones'] });
    await cliente.invalidateQueries({ queryKey: ['grupos-completo'] });
    await cliente.invalidateQueries({ queryKey: ['grupos'] });
    await cliente.invalidateQueries({ queryKey: ['licencia'] });
  };

  const desactivar = useMutation({
    mutationFn: (id: string) => api.delete(`/usuarios/${id}`),
    onSuccess: async () => {
      await refrescar();
      setDesactivando(null);
    },
  });

  const unMiembro = (grupos ?? []).filter((g) => g.tipo === 'FUNCIONAL' && g.miembros.length === 1);

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Grupos y Usuarios</h1>
          <p className="cabecera-subtitulo">Control de acceso y asignación de trabajo.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {administraGrupos ? (
            <button type="button" className="boton secundario oculto-en-movil" onClick={() => setEditandoGrupo(null)}>
              Nuevo grupo
            </button>
          ) : null}
          {invitaAqui ? (
            <>
              <button type="button" className="boton oculto-en-movil" onClick={() => setEditandoUsuario(null)}>
                Invitar usuario
              </button>
              <button
                type="button"
                className="boton-flotante-crear"
                aria-label="Invitar usuario"
                onClick={() => setEditandoUsuario(null)}
              >
                <IconoMas />
              </button>
            </>
          ) : null}
          {portalUsuarios !== null && administraUsuarios ? (
            // Pestaña nueva y no la misma: el portal vive en otro dominio y esta sesión de CMMS
            // sigue abierta detrás. `noopener` porque es una navegación a otro origen.
            <a className="boton" href={portalUsuarios} target="_blank" rel="noopener noreferrer">
              Gestionar usuarios en Continnua
            </a>
          ) : null}
        </div>
      </div>

      {portalUsuarios !== null ? (
        <Aviso>
          {administraUsuarios
            ? 'Esta organización gestiona sus usuarios en Continnua. Invítalos desde el portal y aparecerán aquí automáticamente, con su acceso ya creado.'
            : 'Esta organización gestiona sus usuarios en Continnua. Pídeselo a quien la administre y aparecerán aquí automáticamente.'}
        </Aviso>
      ) : null}

      {/* §12.6: un grupo funcional de un solo miembro es un alias de esa persona
          y anula el propósito del módulo. */}
      {unMiembro.length > 0 ? (
        <Aviso tono="atencion">
          {unMiembro.length} grupo(s) funcional(es) tienen un solo miembro: {unMiembro.map((g) => g.nombre).join(', ')}.
          Sin varios integrantes no hay escalamiento ni balanceo de carga, y el reporte de utilización
          de mano de obra pierde sentido.
        </Aviso>
      ) : null}

      <section className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-cabecera">
          <h2>Grupos</h2>
          <span className="nav-contador">{grupos?.length ?? 0}</span>
        </div>
        {esMovil ? (
          <div className="tarjetas-ot">
            {(grupos ?? []).map((g) => (
              <div key={g.id} className="tarjeta-ot">
                <div className="tarjeta-ot-cuerpo">
                  <div className="tarjeta-ot-cabecera">
                    <div className="tarjeta-ot-titulo">{g.nombre}</div>
                    <Etiqueta tono={g.tipo === 'BANDEJA_ENTRADA' ? 'azul' : 'gris'}>
                      {ETIQUETA_TIPO_GRUPO[g.tipo]}
                    </Etiqueta>
                  </div>
                  <div className="tarjeta-ot-nota">
                    {(g.miembros ?? []).map((m) => m.nombreCompleto).join(', ') || 'Sin miembros fijos'}
                  </div>
                  <div className="tarjeta-ot-datos">
                    <span>{g.ordenesAbiertas} orden(es) abierta(s)</span>
                  </div>
                </div>
                {administraGrupos ? (
                  <div className="acciones-fila">
                    <button type="button" className="boton secundario pequeno" onClick={() => setEditandoGrupo(g)}>
                      Editar
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th>Tipo</th>
                  <th>Miembros</th>
                  <th>Órdenes abiertas</th>
                  {administraGrupos ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {(grupos ?? []).map((g) => (
                  <tr key={g.id}>
                    <td style={{ fontWeight: 600 }}>{g.nombre}</td>
                    <td>
                      <Etiqueta tono={g.tipo === 'BANDEJA_ENTRADA' ? 'azul' : 'gris'}>
                        {ETIQUETA_TIPO_GRUPO[g.tipo]}
                      </Etiqueta>
                    </td>
                    <td>{(g.miembros ?? []).map((m) => m.nombreCompleto).join(', ') || 'Sin miembros fijos'}</td>
                    <td className="numero-registro">{g.ordenesAbiertas}</td>
                    {administraGrupos ? (
                      <td>
                        <div className="acciones-fila">
                          <button type="button" className="boton secundario pequeno" onClick={() => setEditandoGrupo(g)}>
                            Editar
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {grupos && grupos.length === 0 ? (
          <Vacio
            titulo="Sin grupos"
            detalle={administraGrupos ? 'Cree el primero con «Nuevo grupo».' : undefined}
          />
        ) : null}
      </section>

      <section className="panel">
        <div className="panel-cabecera">
          <h2>Usuarios</h2>
          <span className="nav-contador">{usuarios?.total ?? 0}</span>
        </div>

        {isLoading ? <Cargando /> : null}

        {esMovil ? (
          <div className="tarjetas-ot">
            {(usuarios?.datos ?? []).map((u) => (
              <div key={u.id} className="tarjeta-ot" style={u.activo ? undefined : { opacity: 0.55 }}>
                <div className="tarjeta-ot-cuerpo">
                  <div className="tarjeta-ot-cabecera">
                    <div className="tarjeta-ot-titulo">{u.nombreCompleto}</div>
                    {u.consumeAsiento ? (
                      <Etiqueta tono="azul">Facturable</Etiqueta>
                    ) : (
                      <Etiqueta tono="verde">Gratuito</Etiqueta>
                    )}
                  </div>
                  {u.email ? <div className="tarjeta-ot-nota">{u.email}</div> : null}
                  <div className="tarjeta-ot-datos">
                    <span>{ETIQUETA_TIPO_ACCESO[u.tipoAcceso]}</span>
                    {u.ubicacionAsignada ? <span>{u.ubicacionAsignada}</span> : null}
                    {u.grupos.length > 0 ? <span>{u.grupos.map((g) => g.nombre).join(', ')}</span> : null}
                    <span>{u.ultimaVisita ? formatearFechaHora(u.ultimaVisita) : 'Nunca ingresó'}</span>
                  </div>
                </div>
                {administraUsuarios ? (
                  <div className="acciones-fila">
                    <button
                      type="button"
                      className="boton secundario pequeno"
                      onClick={() => setEditandoUsuario(u)}
                    >
                      Editar
                    </button>
                    {u.ubicacionAsignadaId ? (
                      <button type="button" className="boton secundario pequeno" onClick={() => setTabletsDe(u)}>
                        Tablets
                      </button>
                    ) : null}
                    {u.activo ? (
                      <button
                        type="button"
                        className="boton secundario pequeno"
                        onClick={() => setDesactivando(u)}
                      >
                        Desactivar
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo de acceso</th>
                  <th>Asiento</th>
                  <th>Sede asignada</th>
                  <th>Grupos</th>
                  <th>Última visita</th>
                  {administraUsuarios ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {(usuarios?.datos ?? []).map((u) => (
                  <tr key={u.id} style={u.activo ? undefined : { opacity: 0.55 }}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.nombreCompleto}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{u.email ?? '—'}</div>
                    </td>
                    <td>{ETIQUETA_TIPO_ACCESO[u.tipoAcceso]}</td>
                    <td>
                      {u.consumeAsiento ? (
                        <Etiqueta tono="azul">Facturable</Etiqueta>
                      ) : (
                        <Etiqueta tono="verde">Gratuito</Etiqueta>
                      )}
                    </td>
                    <td>{u.ubicacionAsignada ?? '—'}</td>
                    <td>{u.grupos.map((g) => g.nombre).join(', ') || '—'}</td>
                    <td>{u.ultimaVisita ? formatearFechaHora(u.ultimaVisita) : 'Nunca ingresó'}</td>
                    {administraUsuarios ? (
                      <td>
                        <div className="acciones-fila">
                          <button
                            type="button"
                            className="boton secundario pequeno"
                            onClick={() => setEditandoUsuario(u)}
                          >
                            Editar
                          </button>
                          {u.ubicacionAsignadaId ? (
                            <button type="button" className="boton secundario pequeno" onClick={() => setTabletsDe(u)}>
                              Tablets
                            </button>
                          ) : null}
                          {u.activo ? (
                            <button
                              type="button"
                              className="boton secundario pequeno"
                              onClick={() => setDesactivando(u)}
                            >
                              Desactivar
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {usuarios ? (
          <Paginacion pagina={usuarios.pagina} tamano={usuarios.tamano} total={usuarios.total} onCambiar={setPagina} />
        ) : null}
      </section>

      {tabletsDe ? <DialogoTabletsSede usuario={tabletsDe} alCerrar={() => setTabletsDe(null)} /> : null}

      {editandoUsuario !== undefined ? (
        <FormularioUsuario
          usuario={editandoUsuario}
          alCerrar={() => setEditandoUsuario(undefined)}
          alGuardar={refrescar}
        />
      ) : null}

      {editandoGrupo !== undefined ? (
        <FormularioGrupo
          grupo={editandoGrupo}
          alCerrar={() => setEditandoGrupo(undefined)}
          alGuardar={async () => {
            await refrescar();
            setEditandoGrupo(undefined);
          }}
        />
      ) : null}

      {desactivando ? (
        <DialogoConfirmar
          titulo="Desactivar usuario"
          etiqueta="Desactivar"
          mensaje={`${desactivando.nombreCompleto} dejará de poder ingresar${
            desactivando.consumeAsiento ? ' y su asiento vuelve a la licencia' : ''
          }. El histórico de su trabajo se conserva.`}
          cargando={desactivar.isPending}
          error={desactivar.error}
          alCerrar={() => {
            desactivar.reset();
            setDesactivando(null);
          }}
          alConfirmar={() => desactivar.mutate(desactivando.id)}
        />
      ) : null}
    </>
  );
}

/**
 * Alta por invitación y edición de un usuario (§12.4).
 *
 * El alta devuelve el token una sola vez —en la base solo queda su hash—, así
 * que el diálogo se queda abierto mostrándolo: cerrarlo sin copiarlo obliga a
 * reinvitar.
 */
function FormularioUsuario({
  usuario,
  alCerrar,
  alGuardar,
}: {
  usuario: Usuario | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const { puede } = useSesion();
  const [nombreCompleto, setNombreCompleto] = useState(usuario?.nombreCompleto ?? '');
  const [email, setEmail] = useState(usuario?.email ?? '');
  const [telefonoMovil, setTelefonoMovil] = useState(usuario?.telefonoMovil ?? '');
  const [tipoAcceso, setTipoAcceso] = useState<TipoAcceso>(usuario?.tipoAcceso ?? 'SOLICITANTE');
  const [ubicacionAsignadaId, setUbicacionAsignadaId] = useState(usuario?.ubicacionAsignadaId ?? '');
  const [grupos, setGrupos] = useState<string[]>(usuario?.grupos.map((g) => g.id) ?? []);
  const [activo, setActivo] = useState(usuario?.activo ?? true);
  const [esCompartido, setEsCompartido] = useState(usuario?.esCompartido ?? false);
  const [invitacion, setInvitacion] = useState<string | null>(null);

  const { data: ubicaciones } = useUbicaciones();
  const { data: opcionesGrupo } = useGruposOpciones(puede('grupo:leer'));

  const guardar = useMutation({
    mutationFn: async () => {
      const contacto: { email?: string; telefonoMovil?: string } = {};
      // Los contactos vacíos se omiten: el contrato valida el formato y una
      // cadena vacía sería un correo inválido, no un «sin correo».
      if (texto(email) !== '') contacto.email = texto(email);
      if (texto(telefonoMovil) !== '') contacto.telefonoMovil = texto(telefonoMovil);

      const base = {
        nombreCompleto: texto(nombreCompleto),
        ...contacto,
        tipoAcceso,
        ubicacionAsignadaId: referencia(ubicacionAsignadaId),
        grupos,
        esCompartido,
      };

      if (usuario) return api.put<Usuario>(`/usuarios/${usuario.id}`, { ...base, activo });
      return api.post<{ usuario: Usuario; tokenInvitacion: string }>('/usuarios', base);
    },
    onSuccess: async (respuesta) => {
      await alGuardar();
      if (usuario) alCerrar();
      else if (respuesta && 'tokenInvitacion' in respuesta) setInvitacion(respuesta.tokenInvitacion);
    },
  });

  const sinContacto = texto(email) === '' && texto(telefonoMovil) === '';

  if (invitacion) {
    const enlace = `${window.location.origin}/invitacion/${invitacion}`;
    return (
      <Dialogo titulo="Usuario invitado" ancho={560} alCerrar={alCerrar}>
        <Aviso tono="exito">
          {nombreCompleto} quedó registrado. Este enlace se muestra una sola vez: cópielo ahora y
          hágaselo llegar.
        </Aviso>
        <div className="campo">
          <label htmlFor="invitacion-enlace">Enlace de invitación</label>
          <input id="invitacion-enlace" value={enlace} readOnly onFocus={(e) => e.currentTarget.select()} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="boton" onClick={alCerrar}>
            Listo
          </button>
        </div>
      </Dialogo>
    );
  }

  return (
    <Dialogo titulo={usuario ? `Editar ${usuario.nombreCompleto}` : 'Invitar usuario'} ancho={560} alCerrar={alCerrar}>
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="usuario-nombre">Nombre completo</label>
        <input
          id="usuario-nombre"
          value={nombreCompleto}
          onChange={(e) => setNombreCompleto(e.target.value)}
          maxLength={200}
          required
        />
      </div>

      <div className="rejilla-campos">
        <div className="campo">
          <label htmlFor="usuario-email">Correo</label>
          <input id="usuario-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="usuario-telefono">Teléfono móvil</label>
          <input id="usuario-telefono" value={telefonoMovil} onChange={(e) => setTelefonoMovil(e.target.value)} />
        </div>
      </div>

      <div className="campo">
        <SelectorLista
          id="usuario-acceso"
          etiqueta="Tipo de acceso"
          valor={tipoAcceso}
          alCambiar={(v) => setTipoAcceso(v as TipoAcceso)}
          opciones={TIPOS_ACCESO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_ACCESO[t] }))}
          requerido
        />
        {/* Solo Administrador y Full User consumen asiento: decirlo aquí evita
            descubrir el cargo recién en la factura. */}
        <span className="campo-nota">
          {tipoAcceso === 'SOLICITANTE'
            ? 'No consume asiento de la licencia.'
            : 'Consume un asiento facturable de la licencia.'}
        </span>
      </div>

      <SelectorUbicacion
        id="usuario-sede"
        etiqueta="Sede asignada"
        valor={ubicacionAsignadaId}
        alCambiar={setUbicacionAsignadaId}
        ubicaciones={ubicaciones}
      />

      {puede('grupo:leer') ? (
        <SeleccionMultiple
          etiqueta="Grupos"
          opciones={(opcionesGrupo ?? []).map((g) => ({ id: g.id, nombre: g.nombre }))}
          seleccion={grupos}
          alCambiar={setGrupos}
          vacio="Aún no hay grupos."
        />
      ) : null}

      {usuario ? (
        <label className="casilla" style={{ marginBottom: 14 }}>
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          <span>Cuenta activa</span>
        </label>
      ) : null}

      <label className="casilla" style={{ marginBottom: 4 }}>
        <input type="checkbox" checked={esCompartido} onChange={(e) => setEsCompartido(e.target.checked)} />
        <span>Cuenta compartida (usada por varias personas, p. ej. la de una sede)</span>
      </label>
      <p style={{ marginTop: 0, marginBottom: 14, fontSize: 12.5, color: 'var(--texto-tenue)' }}>
        Al crear una solicitud desde esta cuenta, se exigirá el nombre de la persona que la hace.
      </p>

      {sinContacto ? (
        <Aviso tono="atencion">Se requiere un correo o un teléfono móvil: es la vía por la que llega la invitación.</Aviso>
      ) : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={texto(nombreCompleto) === '' || sinContacto}
        etiqueta={usuario ? 'Guardar cambios' : 'Invitar'}
      />
    </Dialogo>
  );
}

function FormularioGrupo({
  grupo,
  alCerrar,
  alGuardar,
}: {
  grupo: Grupo | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(grupo?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(grupo?.descripcion ?? '');
  const [tipo, setTipo] = useState<TipoGrupo>(grupo?.tipo ?? 'FUNCIONAL');
  const [administradorId, setAdministradorId] = useState(grupo?.administradorId ?? '');
  const [miembros, setMiembros] = useState<string[]>(grupo?.miembros.map((m) => m.id) ?? []);
  const [confirmarMiembroUnico, setConfirmarMiembroUnico] = useState(false);
  const [accesoMateriales, setAccesoMateriales] = useState(grupo?.accesoMateriales ?? false);

  const { data: usuarios } = useUsuariosOpciones();

  const guardar = useMutation({
    mutationFn: (confirmado: boolean) => {
      const cuerpo = {
        nombre: texto(nombre),
        descripcion: texto(descripcion),
        tipo,
        administradorId: referencia(administradorId),
        miembros,
        accesoMateriales,
        confirmarMiembroUnico: confirmado,
        // Ya no es editable acá (§ pedido: sin efecto real, se sacó del
        // formulario) — se reenvía tal cual venía para no borrar silenciosamente
        // lo que ya hubiera guardado un grupo existente.
        // Ídem (§ pedido): "Procedimientos a cargo" se edita solo desde la
        // ficha del procedimiento, no acá — se reenvía sin cambios.
        tiposActivoId: grupo?.tiposActivo.map((t) => t.id) ?? [],
        procedimientosId: grupo?.procedimientos.map((p) => p.id) ?? [],
      };
      return grupo ? api.put<Grupo>(`/grupos/${grupo.id}`, cuerpo) : api.post<Grupo>('/grupos', cuerpo);
    },
    onSuccess: alGuardar,
    onError: (error: unknown) => {
      if (error instanceof ErrorApi && error.codigo === CODIGOS_ERROR.GRUPO_MIEMBRO_UNICO) {
        setConfirmarMiembroUnico(true);
      }
    },
  });

  const miembroUnico =
    guardar.error instanceof ErrorApi && guardar.error.codigo === CODIGOS_ERROR.GRUPO_MIEMBRO_UNICO;

  return (
    <Dialogo titulo={grupo ? `Editar ${grupo.nombre}` : 'Nuevo grupo'} ancho={560} alCerrar={alCerrar}>
      {guardar.isError && !miembroUnico ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}
      {miembroUnico ? <Aviso tono="atencion">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="grupo-nombre">Nombre</label>
        <input
          id="grupo-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={150}
          required
          placeholder="Ej.: Mantenimiento Lima Este"
        />
      </div>

      <div className="rejilla-campos">
        <SelectorLista
          id="grupo-tipo"
          etiqueta="Tipo"
          valor={tipo}
          alCambiar={(v) => setTipo(v as TipoGrupo)}
          opciones={TIPOS_GRUPO.map((t) => ({ id: t, nombre: ETIQUETA_TIPO_GRUPO[t] }))}
          requerido
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'end', minHeight: 40 }}>
          <input type="checkbox" checked={accesoMateriales} onChange={(e) => setAccesoMateriales(e.target.checked)} />
          <span>
            Acceso a listas de materiales
            <span className="campo-nota" style={{ display: 'block' }}>
              Sus miembros ven y editan las listas y los materiales de cada OT.
            </span>
          </span>
        </label>

        <SelectorLista
          id="grupo-administrador"
          etiqueta="Administrador"
          valor={administradorId}
          alCambiar={setAdministradorId}
          opciones={(usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
          textoVacio="Sin administrador"
        />
      </div>

      <SeleccionMultiple
        etiqueta="Miembros"
        opciones={(usuarios ?? []).map((u) => ({ id: u.id, nombre: u.nombre }))}
        seleccion={miembros}
        alCambiar={(ids) => {
          setMiembros(ids);
          setConfirmarMiembroUnico(false);
          guardar.reset();
        }}
        vacio="Aún no hay usuarios activos."
      />

      <div className="campo">
        <label htmlFor="grupo-descripcion">Descripción</label>
        <textarea
          id="grupo-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={2000}
        />
      </div>

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate(confirmarMiembroUnico)}
        cargando={guardar.isPending}
        deshabilitado={texto(nombre) === ''}
        etiqueta={confirmarMiembroUnico ? 'Crear de todos modos' : grupo ? 'Guardar cambios' : 'Crear grupo'}
      />
    </Dialogo>
  );
}
